import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import { dateTimeToMonDDYYYYWithTime, dateToMonDDYYYY } from '../utils/dateUtils';
import {
  courseRawToTaxonomyCode,
  filterEnrollmentsForSemesterComparator,
  enrollmentMatchesTaxonomyCode,
  parseDelimitedRows,
  findColumnIndex,
  studentIsS1OnlyForS2Comparator
} from '../utils/courseComparatorUtils';

// Helper function to format date fields (YYYY-MM-DD format)
const formatDate = (dateString) => {
  if (!dateString) return '-';
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateToMonDDYYYY(dateString);
  }
  return dateTimeToMonDDYYYYWithTime(dateString, false);
};

// Helper function to format date with time (for "Added at")
const formatDateWithTime = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString();
};

const CourseEnrollManager = ({ userData }) => {
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [students, setStudents] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [courseEnrollments, setCourseEnrollments] = useState([]);
  const [courseGroups, setCourseGroups] = useState([]);
  const [error, setError] = useState('');
  
  // Filters and search
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Course Groups state
  const [showCourseGroupForm, setShowCourseGroupForm] = useState(false);
  const [editingCourseGroupId, setEditingCourseGroupId] = useState(null); // Track which group is being edited
  const [showBulkEnrollForm, setShowBulkEnrollForm] = useState(false);
  const [selectedCourseGroup, setSelectedCourseGroup] = useState('');
  const [isCourseGroupPreSelected, setIsCourseGroupPreSelected] = useState(false);
  const [selectedStudentsForBulk, setSelectedStudentsForBulk] = useState([]);
  const [bulkStudentSearch, setBulkStudentSearch] = useState('');
  const [bulkCsvText, setBulkCsvText] = useState('');
  const [courseGroupFormData, setCourseGroupFormData] = useState({
    title: '',
    courses: [],
    setup: []
  });
  
  // Course Groups expand/collapse state
  const [expandedCourseGroups, setExpandedCourseGroups] = useState(new Set());
  const [allExpanded, setAllExpanded] = useState(false);
  
  // Course Enrollments expand/collapse state
  const [courseEnrollmentsExpanded, setCourseEnrollmentsExpanded] = useState(false);
  
  // Course Enrollments pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  
  // Bulk delete state
  const [selectedEnrollments, setSelectedEnrollments] = useState(new Set());
  
  // Roster modal state
  const [showRosterModal, setShowRosterModal] = useState(false);
  const [selectedRosterCourseGroup, setSelectedRosterCourseGroup] = useState(null);
  const [expandedEnrollments, setExpandedEnrollments] = useState(new Set());
  const [rosterMatchMode, setRosterMatchMode] = useState(false);
  const [rosterSelectedMissing, setRosterSelectedMissing] = useState(new Set());
  
  // Comparator modal state
  const [showComparatorModal, setShowComparatorModal] = useState(false);
  const [comparatorCsvText, setComparatorCsvText] = useState('');
  const [comparatorResults, setComparatorResults] = useState(null);
  const [comparatorFilter, setComparatorFilter] = useState(null); // null, 'match', 'mismatch', 'not_found', 'ignored'
  const [comparatorSortColumn, setComparatorSortColumn] = useState(null); // null, 'course', 'email', 'status', 'row'
  const [comparatorSortOrder, setComparatorSortOrder] = useState('asc'); // 'asc', 'desc'
  /** Main sheet: course-raw + optional school level; legacy: CSV "course" = course group title */
  const [comparatorSourceMode, setComparatorSourceMode] = useState('main_course_raw');
  /** S2-only: ignore enrollments in course groups whose title is clearly S1 */
  const [comparatorSemesterMode, setComparatorSemesterMode] = useState('s2_only');
  /** S1/S2 segment inside taxonomy code from course-raw (default S2 for spring placement) */
  const [comparatorTaxonomySemester, setComparatorTaxonomySemester] = useState('S2');

  // Audit modal state
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [auditActiveTab, setAuditActiveTab] = useState('missing'); // 'missing' = Tab 1
  const [auditMinEnrolled, setAuditMinEnrolled] = useState(''); // filter: show only if enrolled in more than X
  const [auditSelectedMissing, setAuditSelectedMissing] = useState(new Set()); // Set of 'enrollmentId|courseGroupId'
  const [auditIgnoreIfAnyNonEnrolled, setAuditIgnoreIfAnyNonEnrolled] = useState(true); // ignore rows where student has any course in group with status !== 'enrolled'

  // Withdrawal modal state
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [enrollmentToWithdraw, setEnrollmentToWithdraw] = useState(null);

  const [searchParams] = useSearchParams();
  const deletionsAllowed = searchParams.get('deletions') === 'allowed';

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  // Reset to page 1 and clear selections when filters change
  useEffect(() => {
    setCurrentPage(1);
    setSelectedEnrollments(new Set());
  }, [selectedCourse, selectedStudent, statusFilter, searchTerm]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      const [managerDataResponse, courseGroupsResponse] = await Promise.all([
        axios.get('/api/course-enrollments/manager-data', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get('/api/course-groups', {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setCourses(managerDataResponse.data.courses || []);
      setStudents(managerDataResponse.data.students || []);
      setEnrollments(managerDataResponse.data.enrollments || []);
      setCourseEnrollments(managerDataResponse.data.courseEnrollments || []);
      setCourseGroups(courseGroupsResponse.data || []);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Filter course enrollments based on selected filters
  const filteredCourseEnrollments = courseEnrollments.filter(ce => {
    const matchesCourse = !selectedCourse || ce.course === selectedCourse;
    const matchesStudent = !selectedStudent || ce.enrollment === selectedStudent;
    const matchesStatus = !statusFilter || ce.status === statusFilter;
    
    // Search by student name and course name
    const studentName = `${ce.first_name || ''} ${ce.last_name || ''}`.toLowerCase();
    const courseName = (ce.course_title || '').toLowerCase();
    const matchesSearch = !searchTerm || 
      studentName.includes(searchTerm.toLowerCase()) ||
      courseName.includes(searchTerm.toLowerCase());

    return matchesCourse && matchesStudent && matchesStatus && matchesSearch;
  });

  // Handle course group creation or update
  const handleCreateCourseGroup = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      if (editingCourseGroupId) {
        // Update existing course group
        await axios.put(`/api/course-groups/${editingCourseGroupId}`, {
          title: courseGroupFormData.title,
          courses: courseGroupFormData.courses,
          setup: courseGroupFormData.setup
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Create new course group
        await axios.post('/api/course-groups', {
          title: courseGroupFormData.title,
          courses: courseGroupFormData.courses,
          setup: courseGroupFormData.setup,
          createdby: userData.account_id
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      // Reset form and reload data
      setCourseGroupFormData({
        title: '',
        courses: [],
        setup: []
      });
      setEditingCourseGroupId(null);
      setShowCourseGroupForm(false);
      loadData();
    } catch (err) {
      console.error('Error saving course group:', err);
      setError(editingCourseGroupId ? 'Failed to update course group' : 'Failed to create course group');
    }
  };

  // Handle editing a course group
  const handleEditCourseGroup = async (group) => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch the full course group data
      const response = await axios.get(`/api/course-groups/${group.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const groupData = response.data;
      
      // Populate form with group data
      setCourseGroupFormData({
        title: groupData.title || '',
        courses: groupData.courses || [],
        setup: groupData.courses_with_dates?.map(course => ({
          course_id: course.id,
          start_date: course.start_date || '',
          end_date: course.end_date || ''
        })) || []
      });
      
      setEditingCourseGroupId(group.id);
      setShowCourseGroupForm(true);
    } catch (err) {
      console.error('Error loading course group for editing:', err);
      setError('Failed to load course group for editing');
    }
  };

  // Clone course group (copy title/courses/setup; do not copy createddate/updateddate; title editable)
  const handleCloneCourseGroup = async (group) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/course-groups/${group.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const groupData = response.data;
      setCourseGroupFormData({
        title: `Copy of ${groupData.title || ''}`,
        courses: groupData.courses || [],
        setup: (groupData.courses_with_dates || []).map(course => ({
          course_id: course.id,
          start_date: course.start_date || '',
          end_date: course.end_date || ''
        }))
      });
      setEditingCourseGroupId(null); // Create new; server will set createddate/updateddate
      setShowCourseGroupForm(true);
    } catch (err) {
      console.error('Error loading course group for clone:', err);
      setError('Failed to load course group for cloning');
    }
  };

  // Check if a course group has any course that does not allow enrollment (modlock !== true)
  const groupHasUnlockedCourses = (group) => {
    const groupCourseIds = group?.courses || (group?.courses_with_dates || []).map(cd => cd.id || cd.course_id).filter(Boolean);
    return groupCourseIds.some(cid => {
      const c = courses.find(cf => cf.id === cid);
      return !c || c.modlock !== true;
    });
  };

  // Handle bulk enrollment
  const handleBulkEnroll = async (e) => {
    e.preventDefault();
    const selectedGroup = courseGroups.find(g => g.id === selectedCourseGroup);
    if (selectedGroup && groupHasUnlockedCourses(selectedGroup)) {
      setError('Enrollment is not allowed for one or more courses in this group because they are not locked for modifications.');
      return;
    }
    try {
      setError('');
      const token = localStorage.getItem('token');
      await axios.post(`/api/course-groups/${selectedCourseGroup}/enroll`, {
        studentIds: selectedStudentsForBulk
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Reset form and reload data
      setSelectedCourseGroup('');
      setIsCourseGroupPreSelected(false);
      setSelectedStudentsForBulk([]);
      setBulkStudentSearch('');
      setBulkCsvText('');
      setShowBulkEnrollForm(false);
      loadData();
    } catch (err) {
      console.error('Error bulk enrolling students:', err);
      setError(err.response?.data?.error || 'Failed to bulk enroll students');
    }
  };

  // Add course to group (adds to end of list)
  const addCourseToGroup = (courseId) => {
    if (!courseGroupFormData.courses.includes(courseId)) {
      const newCourses = [...courseGroupFormData.courses, courseId];
      const newSetup = [...courseGroupFormData.setup, {
        course_id: courseId,
        start_date: '',
        end_date: ''
      }];
      setCourseGroupFormData({
        ...courseGroupFormData,
        courses: newCourses,
        setup: newSetup
      });
    }
  };

  // Move course up in order
  const moveCourseUp = (index) => {
    if (index === 0) return; // Can't move first item up
    const newCourses = [...courseGroupFormData.courses];
    const newSetup = [...courseGroupFormData.setup];
    
    // Swap courses
    [newCourses[index - 1], newCourses[index]] = [newCourses[index], newCourses[index - 1]];
    
    // Swap setups
    const setup1Index = newSetup.findIndex(s => s.course_id === newCourses[index - 1]);
    const setup2Index = newSetup.findIndex(s => s.course_id === newCourses[index]);
    if (setup1Index !== -1 && setup2Index !== -1) {
      [newSetup[setup1Index], newSetup[setup2Index]] = [newSetup[setup2Index], newSetup[setup1Index]];
    }
    
    setCourseGroupFormData({
      ...courseGroupFormData,
      courses: newCourses,
      setup: newSetup
    });
  };

  // Move course down in order
  const moveCourseDown = (index) => {
    if (index === courseGroupFormData.courses.length - 1) return; // Can't move last item down
    const newCourses = [...courseGroupFormData.courses];
    const newSetup = [...courseGroupFormData.setup];
    
    // Swap courses
    [newCourses[index], newCourses[index + 1]] = [newCourses[index + 1], newCourses[index]];
    
    // Swap setups
    const setup1Index = newSetup.findIndex(s => s.course_id === newCourses[index]);
    const setup2Index = newSetup.findIndex(s => s.course_id === newCourses[index + 1]);
    if (setup1Index !== -1 && setup2Index !== -1) {
      [newSetup[setup1Index], newSetup[setup2Index]] = [newSetup[setup2Index], newSetup[setup1Index]];
    }
    
    setCourseGroupFormData({
      ...courseGroupFormData,
      courses: newCourses,
      setup: newSetup
    });
  };

  // Load template sequence based on first course
  const handleLoadTemplateSequence = () => {
    if (courseGroupFormData.courses.length === 0) {
      alert('Please select at least one course first');
      return;
    }

    // Get the first course (index 0)
    const firstCourseId = courseGroupFormData.courses[0];
    const firstCourse = courses.find(c => c.id === firstCourseId);
    
    if (!firstCourse) {
      alert('First course not found');
      return;
    }

    // Define the sequence wheel
    const sequenceWheel = [
      'Media Broadcasting',
      'Graphic Design',
      'Photography',
      'Digital Marketing',
      'Recording Technology',
      'Video Production'
    ];

    // Extract base course name (remove 1A, 2A, (MS), (HS) notations)
    const title = firstCourse.title || '';
    let baseCourseName = '';
    let has1A = false;
    let has2A = false;
    let hasMS = false;
    let hasHS = false;

    // Check for notations
    if (title.includes('1A')) has1A = true;
    if (title.includes('2A')) has2A = true;
    if (title.includes('(MS)')) hasMS = true;
    if (title.includes('(HS)')) hasHS = true;

    // Extract base name by removing notations
    baseCourseName = title
      .replace(/1A/gi, '')
      .replace(/2A/gi, '')
      .replace(/\(MS\)/gi, '')
      .replace(/\(HS\)/gi, '')
      .trim();

    // Find which sequence position the first course is in
    let startIndex = -1;
    for (let i = 0; i < sequenceWheel.length; i++) {
      if (baseCourseName.includes(sequenceWheel[i])) {
        startIndex = i;
        break;
      }
    }

    if (startIndex === -1) {
      alert(`Could not identify course sequence. First course "${title}" doesn't match any template course.`);
      return;
    }

    // Build the full sequence starting from the identified course
    const sequence = [];
    for (let i = 0; i < sequenceWheel.length; i++) {
      const wheelIndex = (startIndex + i) % sequenceWheel.length;
      const baseName = sequenceWheel[wheelIndex];
      
      // For Video Production and Recording Technology, always use 1A (MS) or 1A (HS)
      const isSpecialCourse = baseName === 'Video Production' || baseName === 'Recording Technology';
      
      // Determine notation
      let notation = '';
      if (isSpecialCourse) {
        // Special courses always use 1A (MS) or 1A (HS)
        notation = hasMS ? '1A (MS)' : '1A (HS)';
      } else {
        // Other courses use the same notation as the first course
        if (has1A && hasMS) notation = '1A (MS)';
        else if (has1A && hasHS) notation = '1A (HS)';
        else if (has2A && hasMS) notation = '2A (MS)';
        else if (has2A && hasHS) notation = '2A (HS)';
        else if (has1A) notation = '1A';
        else if (has2A) notation = '2A';
        else if (hasMS) notation = '(MS)';
        else if (hasHS) notation = '(HS)';
      }

      // Find matching course in the available courses
      // Must match both base name and notation (except special courses)
      const matchingCourse = courses.find(c => {
        const cTitle = (c.title || '').trim();
        const normalizedTitle = cTitle
          .replace(/1A/gi, '')
          .replace(/2A/gi, '')
          .replace(/\(MS\)/gi, '')
          .replace(/\(HS\)/gi, '')
          .trim();
        
        // Check if base name matches
        if (!normalizedTitle.includes(baseName)) {
          return false;
        }

        // For special courses (Video Production, Recording Technology), always use 1A but respect MS/HS from first course
        if (isSpecialCourse) {
          const cHas1A = cTitle.includes('1A');
          const cHasMS = cTitle.includes('(MS)');
          const cHasHS = cTitle.includes('(HS)');
          
          // Must have 1A
          if (!cHas1A) return false;
          
          // Must match the MS/HS from the first course
          if (hasMS) {
            return cHasMS && !cHasHS; // First course has (MS), so special course must be 1A (MS)
          } else if (hasHS) {
            return cHasHS && !cHasMS; // First course has (HS), so special course must be 1A (HS)
          } else {
            // First course has no MS/HS notation, but special courses still need 1A with MS or HS
            // Default to MS if available, otherwise HS
            return cHasMS || cHasHS;
          }
        }

        // For other courses, check notation matches the first course
        const cHas1A = cTitle.includes('1A');
        const cHas2A = cTitle.includes('2A');
        const cHasMS = cTitle.includes('(MS)');
        const cHasHS = cTitle.includes('(HS)');

        // Must match the same notation pattern as first course
        if (has1A && hasMS) {
          return cHas1A && cHasMS && !cHas2A;
        } else if (has1A && hasHS) {
          return cHas1A && cHasHS && !cHas2A;
        } else if (has2A && hasMS) {
          return cHas2A && cHasMS && !cHas1A;
        } else if (has2A && hasHS) {
          return cHas2A && cHasHS && !cHas1A;
        } else if (has1A) {
          return cHas1A && !cHas2A;
        } else if (has2A) {
          return cHas2A && !cHas1A;
        } else if (hasMS) {
          return cHasMS && !cHasHS;
        } else if (hasHS) {
          return cHasHS && !cHasMS;
        }

        // If no notation in first course, match courses without notation
        return !cHas1A && !cHas2A && !cHasMS && !cHasHS;
      });

      if (matchingCourse) {
        sequence.push({
          courseId: matchingCourse.id,
          title: matchingCourse.title,
          baseName: baseName
        });
      }
    }

    if (sequence.length === 0) {
      alert('No matching courses found for the template sequence');
      return;
    }

    // Template start dates (MM/DD/YYYY format, converted to YYYY-MM-DD)
    const templateStartDates = [
      '2025-10-06', // Course 1
      '2025-11-03', // Course 2
      '2025-11-17', // Course 3
      '2025-12-08', // Course 4
      '2026-01-05', // Course 5
      '2026-01-19'  // Course 6
    ];

    // Update form data with the sequence
    const newCourses = sequence.map(s => s.courseId);
    const newSetup = sequence.map((s, index) => ({
      course_id: s.courseId,
      start_date: templateStartDates[index] || '',
      end_date: ''
    }));

    setCourseGroupFormData({
      ...courseGroupFormData,
      courses: newCourses,
      setup: newSetup
    });

    alert(`Loaded template sequence with ${sequence.length} courses: ${sequence.map(s => s.baseName).join(' → ')}`);
  };

  // Apply 2026 start-date preset only (does not change courses, only overwrites start_date in setup)
  const TEMPLATE_2026_START_DATES = [
    '2026-02-02',
    '2026-03-02',
    '2026-03-16',
    '2026-03-30',
    '2026-04-20',
    '2026-05-04'
  ];
  const handleApply2026StartDates = () => {
    if (courseGroupFormData.setup.length === 0) {
      alert('No courses in group. Add courses first, or use Load Template Sequence.');
      return;
    }
    const newSetup = courseGroupFormData.setup.map((s, index) => ({
      ...s,
      start_date: TEMPLATE_2026_START_DATES[index] ?? s.start_date
    }));
    setCourseGroupFormData({
      ...courseGroupFormData,
      setup: newSetup
    });
    alert(`Applied 2026 start dates to ${Math.min(courseGroupFormData.setup.length, TEMPLATE_2026_START_DATES.length)} course(s).`);
  };

  // Remove course from group
  const removeCourseFromGroup = (courseId) => {
    const newCourses = courseGroupFormData.courses.filter(id => id !== courseId);
    const newSetup = courseGroupFormData.setup.filter(s => s.course_id !== courseId);
    setCourseGroupFormData({
      ...courseGroupFormData,
      courses: newCourses,
      setup: newSetup
    });
  };

  // Update course setup in group
  const updateCourseSetup = (courseId, field, value) => {
    const newSetup = courseGroupFormData.setup.map(s => 
      s.course_id === courseId ? { ...s, [field]: value } : s
    );
    setCourseGroupFormData({
      ...courseGroupFormData,
      setup: newSetup
    });
  };

  // Toggle student selection for bulk enrollment
  const toggleStudentSelection = (studentId) => {
    if (selectedStudentsForBulk.includes(studentId)) {
      setSelectedStudentsForBulk(selectedStudentsForBulk.filter(id => id !== studentId));
    } else {
      setSelectedStudentsForBulk([...selectedStudentsForBulk, studentId]);
    }
  };

  // Select students from CSV/email list (comma/newline/space/semicolon separated)
  const handleSelectStudentsFromCsv = () => {
    const raw = (bulkCsvText || '').trim();
    if (!raw) return;
    const tokens = raw
      .split(/[\s,;]+/)
      .map(t => t.trim().toLowerCase())
      .filter(Boolean);
    if (tokens.length === 0) return;

    const emailToEnrollmentId = new Map();
    for (const s of students) {
      const email = (s.email || '').toLowerCase();
      if (email) emailToEnrollmentId.set(email, s.enrollment_id);
    }

    const nextSelected = new Set(selectedStudentsForBulk);
    const notFoundEmails = [];
    
    for (const email of tokens) {
      const id = emailToEnrollmentId.get(email);
      if (id !== undefined) {
        nextSelected.add(id);
      } else {
        notFoundEmails.push(email);
      }
    }

    setSelectedStudentsForBulk(Array.from(nextSelected));
    
    // Show popup for emails not found
    if (notFoundEmails.length > 0) {
      const message = `The following emails were not found in the student list:\n\n${notFoundEmails.join('\n')}`;
      alert(message);
    }
  };

  // Toggle course group expand/collapse
  const toggleCourseGroup = (groupId) => {
    setExpandedCourseGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      // Update allExpanded state based on whether all groups are expanded
      const allGroupIds = new Set(courseGroups.map(group => group.id));
      setAllExpanded(newSet.size === allGroupIds.size && allGroupIds.size > 0);
      return newSet;
    });
  };

  // Expand all course groups
  const expandAllCourseGroups = () => {
    const allGroupIds = new Set(courseGroups.map(group => group.id));
    setExpandedCourseGroups(allGroupIds);
    setAllExpanded(true);
  };

  // Minimize all course groups
  const minimizeAllCourseGroups = () => {
    setExpandedCourseGroups(new Set());
    setAllExpanded(false);
  };

  // Handle opening roster modal for a course group
  const handleOpenRoster = (group) => {
    setSelectedRosterCourseGroup(group);
    setShowRosterModal(true);
  };

  // Get enrollments filtered by course group
  const getEnrollmentsByCourseGroup = (courseGroupId) => {
    return courseEnrollments.filter(ce => ce.coursegroup === courseGroupId);
  };

  // Audit: students missing enrollments in courses for a course group they're in
  const missingEnrollmentsAudit = React.useMemo(() => {
    const results = [];
    const courseIdToTitle = new Map();
    courses.forEach(c => courseIdToTitle.set(c.id, c.title || c.id));

    courseGroups.forEach(group => {
      const groupCourseIds = group.courses && group.courses.length
        ? [...group.courses]
        : (group.courses_with_dates || []).map(c => c.id);
      if (groupCourseIds.length === 0) return;

      const enrollmentsInGroup = courseEnrollments.filter(ce => ce.coursegroup === group.id);
      const byStudent = new Map(); // enrollment_id -> { studentInfo, Set of course IDs they're enrolled in }
      enrollmentsInGroup.forEach(ce => {
        const key = ce.enrollment;
        if (!byStudent.has(key)) {
          byStudent.set(key, {
            enrollmentId: ce.enrollment,
            studentName: `${ce.first_name || ''} ${ce.last_name || ''}`.trim() || 'Unknown',
            email: ce.primary_email || '',
            courseGroupId: group.id,
            courseGroupTitle: group.title || '',
            enrolledCourseIds: new Set(),
            enrolledCourseTitles: []
          });
        }
        const rec = byStudent.get(key);
        rec.enrolledCourseIds.add(ce.course);
        if (ce.course_title && !rec.enrolledCourseTitles.includes(ce.course_title)) {
          rec.enrolledCourseTitles.push(ce.course_title);
        }
      });

      byStudent.forEach(rec => {
        const missingIds = groupCourseIds.filter(cid => !rec.enrolledCourseIds.has(cid));
        if (missingIds.length === 0) return;
        const missingTitles = missingIds.map(cid => courseIdToTitle.get(cid) || cid);
        results.push({
          ...rec,
          enrolledCourseIds: undefined,
          totalCourses: groupCourseIds.length,
          enrolledCount: rec.enrolledCourseIds.size,
          missingCourseIds: missingIds,
          missingCourseTitles: missingTitles
        });
      });
    });

    return results;
  }, [courseEnrollments, courseGroups, courses]);

  // Audit: students who have at least one course in a course group with status not 'enrolled' (e.g. withdrawn, dropped)
  const nonEnrolledStatusAudit = React.useMemo(() => {
    const results = [];
    const enrollmentsByStudentAndGroup = new Map(); // key: 'enrollmentId|courseGroupId' -> array of ce

    courseEnrollments.forEach(ce => {
      if (!ce.coursegroup) return;
      const key = `${ce.enrollment}-${ce.coursegroup}`;
      if (!enrollmentsByStudentAndGroup.has(key)) {
        enrollmentsByStudentAndGroup.set(key, []);
      }
      enrollmentsByStudentAndGroup.get(key).push(ce);
    });

    const groupIdToTitle = new Map();
    courseGroups.forEach(g => groupIdToTitle.set(g.id, g.title || ''));

    enrollmentsByStudentAndGroup.forEach((ceList, key) => {
      const nonEnrolled = ceList.filter(ce => ce.status !== 'enrolled');
      if (nonEnrolled.length === 0) return;

      const first = ceList[0];
      const enrolledCount = ceList.filter(ce => ce.status === 'enrolled').length;
      results.push({
        enrollmentId: first.enrollment,
        studentName: `${first.first_name || ''} ${first.last_name || ''}`.trim() || 'Unknown',
        email: first.primary_email || '',
        courseGroupId: first.coursegroup,
        courseGroupTitle: groupIdToTitle.get(first.coursegroup) || first.coursegroup,
        totalInGroup: ceList.length,
        enrolledCount,
        nonEnrolledList: nonEnrolled.map(ce => ({ course_title: ce.course_title || ce.course || 'Unknown', status: ce.status || 'unknown' }))
      });
    });

    return results;
  }, [courseEnrollments, courseGroups]);

  // Keys (enrollmentId-courseGroupId) where that student has at least one course in that group with status !== 'enrolled'
  const studentGroupHasNonEnrolledStatus = React.useMemo(() => {
    const set = new Set();
    courseEnrollments.forEach(ce => {
      if (ce.coursegroup && ce.status !== 'enrolled') set.add(`${ce.enrollment}-${ce.coursegroup}`);
    });
    return set;
  }, [courseEnrollments]);

  // Filter Tab 1 by "enrolled in more than X" and by "ignore if any non-enrolled" (default on)
  const missingEnrollmentsFiltered = React.useMemo(() => {
    let list = missingEnrollmentsAudit;
    if (auditIgnoreIfAnyNonEnrolled) {
      list = list.filter(row => !studentGroupHasNonEnrolledStatus.has(`${row.enrollmentId}-${row.courseGroupId}`));
    }
    const min = auditMinEnrolled.trim() === '' ? null : Number(auditMinEnrolled);
    if (min != null && !isNaN(min)) {
      list = list.filter(row => row.enrolledCount > min);
    }
    return list;
  }, [missingEnrollmentsAudit, auditMinEnrolled, auditIgnoreIfAnyNonEnrolled, studentGroupHasNonEnrolledStatus]);

  const getAuditRowKey = (row) => `${row.enrollmentId}-${row.courseGroupId}`;

  // Bulk enroll selected audit rows: enroll each selected student in their course group (uses group setup start_date)
  const handleAuditBulkEnroll = async () => {
    const selectedKeys = Array.from(auditSelectedMissing);
    if (selectedKeys.length === 0) {
      alert('Please select one or more students to enroll.');
      return;
    }
    const selectedRows = missingEnrollmentsFiltered.filter(row => auditSelectedMissing.has(getAuditRowKey(row)));
    // Group by course group; each group gets list of enrollment IDs (unique)
    const byGroup = new Map();
    selectedRows.forEach(row => {
      if (!byGroup.has(row.courseGroupId)) byGroup.set(row.courseGroupId, new Set());
      byGroup.get(row.courseGroupId).add(row.enrollmentId);
    });
    try {
      const token = localStorage.getItem('token');
      for (const [courseGroupId, enrollmentIds] of byGroup) {
        await axios.post(`/api/course-groups/${courseGroupId}/enroll`, {
          studentIds: Array.from(enrollmentIds),
          missingOnly: true
        }, { headers: { Authorization: `Bearer ${token}` } });
      }
      setAuditSelectedMissing(new Set());
      loadData();
      setShowAuditModal(false);
      alert(`Enrolled ${selectedKeys.length} student(s) in missing courses. Data refreshed.`);
    } catch (err) {
      console.error('Error bulk enrolling from audit:', err);
      setError('Failed to bulk enroll. Please try again.');
    }
  };

  // Roster modal: bulk enroll selected students with missing courses in the roster's course group
  const handleRosterBulkEnroll = async () => {
    if (!selectedRosterCourseGroup || rosterSelectedMissing.size === 0) {
      alert('Please select one or more students to enroll.');
      return;
    }
    const count = rosterSelectedMissing.size;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/course-groups/${selectedRosterCourseGroup.id}/enroll`, {
        studentIds: Array.from(rosterSelectedMissing),
        missingOnly: true
      }, { headers: { Authorization: `Bearer ${token}` } });
      setRosterSelectedMissing(new Set());
      setRosterMatchMode(false);
      loadData();
      alert(`Enrolled ${count} student(s) in missing courses. Data refreshed.`);
    } catch (err) {
      console.error('Error bulk enrolling from roster:', err);
      setError('Failed to bulk enroll. Please try again.');
    }
  };

  const handleWithdrawEnrollment = async () => {
    if (!enrollmentToWithdraw) return;

    try {
      const token = localStorage.getItem('token');
      const ce = enrollmentToWithdraw;

      if (ce.coursegroup) {
        const enrollmentsInGroup = courseEnrollments.filter(
          c => c.enrollment === ce.enrollment && c.coursegroup === ce.coursegroup
        );
        const updatePromises = enrollmentsInGroup.map(e =>
          axios.put(`/api/course-enrollments/${e.id}/status`, { status: 'withdrawn' }, {
            headers: { Authorization: `Bearer ${token}` }
          })
        );
        await Promise.all(updatePromises);
        setCourseEnrollments(prev => prev.map(c =>
          enrollmentsInGroup.some(e => e.id === c.id) ? { ...c, status: 'withdrawn' } : c
        ));
      } else {
        await axios.put(`/api/course-enrollments/${ce.id}/status`, { status: 'withdrawn' }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCourseEnrollments(prev => prev.map(c => c.id === ce.id ? { ...c, status: 'withdrawn' } : c));
      }

      setShowWithdrawalModal(false);
      setEnrollmentToWithdraw(null);
      alert('Student has been withdrawn from the course(s).');
    } catch (err) {
      console.error('Error withdrawing student:', err);
      alert('Failed to withdraw student from course.');
    }
  };

  const handleOpenWithdrawalModal = (ce, e) => {
    e.stopPropagation();
    setEnrollmentToWithdraw(ce);
    setShowWithdrawalModal(true);
  };

  const handleBulkWithdraw = async () => {
    const toWithdraw = Array.from(selectedEnrollments)
      .map(id => courseEnrollments.find(ce => ce.id === id))
      .filter(Boolean)
      .filter(ce => ce.status !== 'withdrawn');
    if (toWithdraw.length === 0) {
      alert('Please select at least one enrollment to withdraw (withdrawn enrollments cannot be selected).');
      return;
    }
    const idsToUpdate = new Set();
    for (const ce of toWithdraw) {
      if (ce.coursegroup) {
        courseEnrollments
          .filter(c => c.enrollment === ce.enrollment && c.coursegroup === ce.coursegroup)
          .forEach(c => idsToUpdate.add(c.id));
      } else {
        idsToUpdate.add(ce.id);
      }
    }
    if (!window.confirm(`Withdraw ${idsToUpdate.size} enrollment(s)?`)) return;
    try {
      const token = localStorage.getItem('token');
      await Promise.all(Array.from(idsToUpdate).map(id =>
        axios.put(`/api/course-enrollments/${id}/status`, { status: 'withdrawn' }, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ));
      setCourseEnrollments(prev => prev.map(c => idsToUpdate.has(c.id) ? { ...c, status: 'withdrawn' } : c));
      setSelectedEnrollments(new Set());
      alert('Selected enrollment(s) have been withdrawn.');
    } catch (err) {
      console.error('Error bulk withdrawing:', err);
      alert('Failed to withdraw some enrollments. Please try again.');
    }
  };

  const handleSelectAllFiltered = () => {
    const nonWithdrawn = filteredCourseEnrollments.filter(ce => ce.status !== 'withdrawn');
    setSelectedEnrollments(prev => {
      const next = new Set(prev);
      nonWithdrawn.forEach(ce => next.add(ce.id));
      return next;
    });
  };

  const handleDeselectAll = () => {
    setSelectedEnrollments(new Set());
  };

  // Handle removing a course enrollment
  const handleRemoveCourseEnrollment = async (enrollmentId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to remove this enrollment?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/course-enrollments/${enrollmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update UI state
      setCourseEnrollments(prev => prev.filter(ce => ce.id !== enrollmentId));
      setSelectedEnrollments(prev => {
        const newSet = new Set(prev);
        newSet.delete(enrollmentId);
        return newSet;
      });
      loadData(); // Reload to ensure consistency
    } catch (err) {
      console.error('Error removing course enrollment:', err);
      alert('Failed to remove enrollment. Please try again.');
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedEnrollments.size === 0) {
      alert('Please select at least one enrollment to delete.');
      return;
    }

    const count = selectedEnrollments.size;
    if (!window.confirm(`Are you sure you want to delete ${count} enrollment(s)?`)) return;

    try {
      const token = localStorage.getItem('token');
      const deletePromises = Array.from(selectedEnrollments).map(enrollmentId =>
        axios.delete(`/api/course-enrollments/${enrollmentId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      );

      await Promise.all(deletePromises);

      // Update UI state
      setCourseEnrollments(prev => prev.filter(ce => !selectedEnrollments.has(ce.id)));
      setSelectedEnrollments(new Set());
      loadData(); // Reload to ensure consistency
    } catch (err) {
      console.error('Error bulk deleting enrollments:', err);
      alert('Failed to delete some enrollments. Please try again.');
    }
  };

  // Toggle select all enrollments (on current page, non-withdrawn only)
  const handleSelectAll = (checked) => {
    setSelectedEnrollments(prev => {
      const newSet = new Set(prev);
      const totalItems = filteredCourseEnrollments.length;
      const totalPages = Math.ceil(totalItems / itemsPerPage);
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedEnrollments = filteredCourseEnrollments.slice(startIndex, endIndex);
      const selectable = paginatedEnrollments.filter(ce => ce.status !== 'withdrawn');

      if (checked) {
        selectable.forEach(ce => newSet.add(ce.id));
      } else {
        selectable.forEach(ce => newSet.delete(ce.id));
      }
      return newSet;
    });
  };

  // Toggle individual enrollment selection
  const handleToggleEnrollment = (enrollmentId) => {
    setSelectedEnrollments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(enrollmentId)) {
        newSet.delete(enrollmentId);
      } else {
        newSet.add(enrollmentId);
      }
      return newSet;
    });
  };

  // Parse CSV text and compare with course enrollments
  const handleCompareCsv = () => {
    if (!comparatorCsvText.trim()) {
      alert('Please enter CSV text to compare.');
      return;
    }

    try {
      const table = parseDelimitedRows(comparatorCsvText.trim());
      if (table.length < 2) {
        alert('Paste must have a header row and at least one data row.');
        return;
      }

      const header = table[0];
      const emailIndex = findColumnIndex(header, ['email', 'student e-mail address', 'e-mail']);
      const statusIndex = findColumnIndex(header, ['status']);
      const courseIndexLegacy = findColumnIndex(header, ['course']);
      const courseRawIndex = findColumnIndex(header, ['course-raw', 'course_raw', 'courseraw', 'course raw']);
      const schoolLevelIndex = findColumnIndex(header, ['school level', 'schoollevel']);
      const gradeLevelIndex = findColumnIndex(header, ['gradelevel', 'grade level', 'grade']);

      if (emailIndex === -1) {
        alert('CSV must include an "email" column.');
        return;
      }

      if (comparatorSourceMode === 'legacy_title') {
        if (courseIndexLegacy === -1) {
          alert('Legacy mode requires a "course" column (exact course group title).');
          return;
        }
      } else if (courseRawIndex === -1) {
        alert('Main export mode requires a "course-raw" column (from the Main sheet). Optional: "School Level" or "gradeLevel".');
        return;
      }

      const taxonomySem = comparatorTaxonomySemester === 'S1' ? 'S1' : 'S2';

      const csvRows = table.slice(1).map((values, index) => {
        const statusValue = statusIndex !== -1 ? (values[statusIndex] || '').toLowerCase().trim() : '';
        const isInactive = statusValue === 'inactive' || statusValue === '0' || statusValue === 'false';

        const email = values[emailIndex] || '';
        const courseLegacy = courseIndexLegacy !== -1 ? (values[courseIndexLegacy] || '') : '';
        const courseRaw = courseRawIndex !== -1 ? (values[courseRawIndex] || '') : '';
        const schoolLevel = schoolLevelIndex !== -1 ? (values[schoolLevelIndex] || '') : '';
        const gradeLevel = gradeLevelIndex !== -1 ? (values[gradeLevelIndex] || '') : '';

        let expectedCode = '';
        if (comparatorSourceMode === 'main_course_raw' && courseRaw) {
          expectedCode = courseRawToTaxonomyCode(courseRaw, schoolLevel, gradeLevel, taxonomySem);
        }

        return {
          rowNumber: index + 2,
          email,
          course: comparatorSourceMode === 'legacy_title' ? courseLegacy : expectedCode || courseRaw,
          courseRaw: comparatorSourceMode === 'main_course_raw' ? courseRaw : '',
          expectedCode: comparatorSourceMode === 'main_course_raw' ? expectedCode : '',
          schoolLevel,
          gradeLevel,
          statusValue,
          isInactive
        };
      }).filter(row => row.email);

      const courseGroupMap = new Map();
      courseGroups.forEach(group => {
        courseGroupMap.set(group.id, group.title);
      });

      const results = csvRows.map(csvRow => {
        const matchingEnrollments = courseEnrollments.filter(ce =>
          ce.primary_email && ce.primary_email.toLowerCase() === csvRow.email.toLowerCase()
        );

        if (csvRow.isInactive) {
          const activeEnrollments = matchingEnrollments.filter(ce =>
            ce.status !== 'withdrawn' && ce.status !== 'dropped'
          );

          if (activeEnrollments.length === 0) {
            const withdrawnCount = matchingEnrollments.filter(ce =>
              ce.status === 'withdrawn' || ce.status === 'dropped'
            ).length;

            return {
              ...csvRow,
              status: 'match',
              message: withdrawnCount > 0
                ? `Status is inactive - already unenrolled (${withdrawnCount} enrollment(s) withdrawn/dropped)`
                : 'Status is inactive - no enrollments found (already unenrolled)',
              matchingEnrollments: [],
              shouldUnenroll: false
            };
          }

          const actualCourseGroups = activeEnrollments
            .map(ce => courseGroupMap.get(ce.coursegroup))
            .filter(Boolean)
            .filter((v, i, a) => a.indexOf(v) === i);

          return {
            ...csvRow,
            status: 'mismatch',
            message: `Status is inactive - should be unenrolled from all courses. Currently enrolled in: ${actualCourseGroups.join(', ') || 'N/A'}`,
            matchingEnrollments: activeEnrollments,
            actualCourseGroups,
            shouldUnenroll: true
          };
        }

        if (matchingEnrollments.length === 0) {
          return {
            ...csvRow,
            status: 'not_found',
            message:
              comparatorSemesterMode === 's2_only'
                ? 'No enrollments on file for this email — count as needs S2 placement (per spreadsheet)'
                : 'No enrollment found for this email',
            matchingEnrollments: []
          };
        }

        const activeMatching = matchingEnrollments.filter(
          ce => ce.status !== 'withdrawn' && ce.status !== 'dropped'
        );

        if (activeMatching.length === 0) {
          return {
            ...csvRow,
            status: 'not_found',
            message:
              comparatorSemesterMode === 's2_only'
                ? 'No active enrollments (withdrawn/dropped only) — count as needs S2 placement'
                : 'No active enrollments for this email (all withdrawn/dropped)',
            matchingEnrollments: []
          };
        }

        if (
          comparatorSemesterMode === 's2_only' &&
          studentIsS1OnlyForS2Comparator(activeMatching, courseGroupMap)
        ) {
          const s1Titles = activeMatching
            .map(ce => courseGroupMap.get(ce.coursegroup))
            .filter(Boolean)
            .filter((v, i, a) => a.indexOf(v) === i);
          return {
            ...csvRow,
            status: 'ignored',
            message: `Only in S1 course group(s) — skipped (S2 comparator). Groups: ${s1Titles.join(', ') || 'N/A'}`,
            matchingEnrollments: activeMatching,
            actualCourseGroups: s1Titles
          };
        }

        const relevantEnrollments = filterEnrollmentsForSemesterComparator(
          activeMatching,
          courseGroupMap,
          comparatorSemesterMode
        );

        if (comparatorSourceMode === 'legacy_title') {
          const legacyMatches = relevantEnrollments.filter(ce => {
            const courseGroupTitle = courseGroupMap.get(ce.coursegroup);
            return courseGroupTitle && courseGroupTitle.toLowerCase() === (csvRow.course || '').toLowerCase();
          });

          if (legacyMatches.length > 0) {
            return {
              ...csvRow,
              status: 'match',
              message: `Found ${legacyMatches.length} matching enrollment(s)`,
              matchingEnrollments: legacyMatches
            };
          }

          const actualCourseGroups = activeMatching
            .map(ce => courseGroupMap.get(ce.coursegroup))
            .filter(Boolean)
            .filter((v, i, a) => a.indexOf(v) === i);

          return {
            ...csvRow,
            status: 'mismatch',
            message: `Email found but enrolled in different course group(s): ${actualCourseGroups.join(', ') || 'N/A'}`,
            matchingEnrollments: activeMatching,
            actualCourseGroups
          };
        }

        // Main sheet: match taxonomy code derived from course-raw
        if (!csvRow.courseRaw || !csvRow.courseRaw.trim()) {
          return {
            ...csvRow,
            status: 'mismatch',
            message: 'Missing course-raw value for this row',
            matchingEnrollments: relevantEnrollments
          };
        }

        if (!csvRow.expectedCode || csvRow.expectedCode === 'UNKNOWN') {
          return {
            ...csvRow,
            status: 'mismatch',
            message: 'Could not map course-raw to a taxonomy code (check spelling / level)',
            matchingEnrollments: relevantEnrollments
          };
        }

        const codeMatches = relevantEnrollments.filter(ce =>
          enrollmentMatchesTaxonomyCode(ce, csvRow.expectedCode, courseGroupMap)
        );

        if (codeMatches.length > 0) {
          return {
            ...csvRow,
            status: 'match',
            message: `Found ${codeMatches.length} enrollment(s) matching expected ${csvRow.expectedCode}`,
            matchingEnrollments: codeMatches
          };
        }

        const actualCourseGroups = activeMatching
          .map(ce => courseGroupMap.get(ce.coursegroup))
          .filter(Boolean)
          .filter((v, i, a) => a.indexOf(v) === i);

        return {
          ...csvRow,
          status: 'mismatch',
          message: `No enrollment in expected ${csvRow.expectedCode} (among ${comparatorSemesterMode === 's2_only' ? 'non-S1' : 'all'} groups). Actual: ${actualCourseGroups.join(', ') || 'N/A'}`,
          matchingEnrollments: relevantEnrollments.length ? relevantEnrollments : activeMatching,
          actualCourseGroups
        };
      });

      setComparatorResults(results);
    } catch (error) {
      console.error('Error comparing CSV:', error);
      alert('Error parsing CSV. Please check the format.');
    }
  };

  // Reset comparator modal
  const handleCloseComparatorModal = () => {
    setShowComparatorModal(false);
    setComparatorCsvText('');
    setComparatorResults(null);
    setComparatorFilter(null);
    setComparatorSortColumn(null);
    setComparatorSortOrder('asc');
    setComparatorSourceMode('main_course_raw');
    setComparatorSemesterMode('s2_only');
    setComparatorTaxonomySemester('S2');
  };

  // Handle comparator column sort
  const handleComparatorSort = (column) => {
    if (comparatorSortColumn === column) {
      // Toggle sort order if clicking the same column
      setComparatorSortOrder(comparatorSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new sort column and default to ascending
      setComparatorSortColumn(column);
      setComparatorSortOrder('asc');
    }
  };

  // Sort comparator results
  const sortComparatorResults = (results) => {
    if (!comparatorSortColumn) return results;

    return [...results].sort((a, b) => {
      let aValue, bValue;

      switch (comparatorSortColumn) {
        case 'course':
          aValue = (a.course || '').toLowerCase();
          bValue = (b.course || '').toLowerCase();
          break;
        case 'email':
          aValue = (a.email || '').toLowerCase();
          bValue = (b.email || '').toLowerCase();
          break;
        case 'status':
          // match < ignored < mismatch < not_found
          const statusPriority = { match: 1, ignored: 2, mismatch: 3, not_found: 4 };
          aValue = statusPriority[a.status] || 5;
          bValue = statusPriority[b.status] || 5;
          break;
        case 'row':
          aValue = a.rowNumber || 0;
          bValue = b.rowNumber || 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return comparatorSortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return comparatorSortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Toggle enrollment expansion in roster modal
  const toggleEnrollmentExpansion = (enrollmentId) => {
    setExpandedEnrollments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(enrollmentId)) {
        newSet.delete(enrollmentId);
      } else {
        newSet.add(enrollmentId);
      }
      return newSet;
    });
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '2rem 1rem' }}>
      <h1 style={{ color: '#1D4E89', fontSize: '2.2rem', margin: '0 0 2rem 0' }}>Course Enrollment Manager</h1>

      {error && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#f8d7da',
          border: '1px solid #f5c6cb',
          borderRadius: 6,
          color: '#721c24',
          marginBottom: 24
        }}>
          {error}
        </div>
      )}

      {/* Course Groups Container */}
      <div style={{
        marginBottom: 32,
        padding: '24px',
        backgroundColor: '#fff',
        borderRadius: 8,
        border: '1px solid #e0e0e0',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ color: '#1D4E89', fontSize: '1.5rem', margin: 0 }}>Course Groups</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => {
                setEditingCourseGroupId(null);
                setCourseGroupFormData({
                  title: '',
                  courses: [],
                  setup: []
                });
                setShowCourseGroupForm(true);
              }}
              style={{
                padding: '0.5rem 0.75rem',
                borderRadius: 6,
                border: '1px solid #e0e0e0',
                background: '#fff',
                color: '#333',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 14,
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#1D4E89';
                e.currentTarget.style.backgroundColor = '#f8f9fa';
                e.currentTarget.style.color = '#1D4E89';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e0e0e0';
                e.currentTarget.style.backgroundColor = '#fff';
                e.currentTarget.style.color = '#333';
              }}
            >
              <span style={{ fontSize: '18px', lineHeight: 1 }}>+</span>
              <span>Create Course Group</span>
            </button>
            <button
              onClick={() => {
                setSelectedCourseGroup('');
                setIsCourseGroupPreSelected(false);
                setSelectedStudentsForBulk([]);
                setBulkStudentSearch('');
                setBulkCsvText('');
                setShowBulkEnrollForm(true);
              }}
              style={{
                padding: '0.5rem 0.75rem',
                borderRadius: 6,
                border: '1px solid #e0e0e0',
                background: '#fff',
                color: '#333',
                cursor: 'pointer',
                fontSize: 14,
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#1D4E89';
                e.currentTarget.style.backgroundColor = '#f8f9fa';
                e.currentTarget.style.color = '#1D4E89';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e0e0e0';
                e.currentTarget.style.backgroundColor = '#fff';
                e.currentTarget.style.color = '#333';
              }}
            >
              Enroll
            </button>
            {courseGroups.length > 0 && (
              <>
                <button
                  onClick={allExpanded ? minimizeAllCourseGroups : expandAllCourseGroups}
                  style={{
                    padding: '0.5rem 0.75rem',
                    borderRadius: 6,
                    border: '1px solid #e0e0e0',
                    background: '#fff',
                    color: '#333',
                    cursor: 'pointer',
                    fontSize: 14,
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#1D4E89';
                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                    e.currentTarget.style.color = '#1D4E89';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e0e0e0';
                    e.currentTarget.style.backgroundColor = '#fff';
                    e.currentTarget.style.color = '#333';
                  }}
                >
                  {allExpanded ? 'Minimize All' : 'Expand All'}
                </button>
                <button
                  onClick={() => setShowComparatorModal(true)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    borderRadius: 6,
                    border: '1px solid #e0e0e0',
                    background: '#fff',
                    color: '#333',
                    cursor: 'pointer',
                    fontSize: 14,
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#1D4E89';
                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                    e.currentTarget.style.color = '#1D4E89';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e0e0e0';
                    e.currentTarget.style.backgroundColor = '#fff';
                    e.currentTarget.style.color = '#333';
                  }}
                >
                  Comparator
                </button>
                <button
                  onClick={() => setShowAuditModal(true)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    borderRadius: 6,
                    border: '1px solid #e0e0e0',
                    background: '#fff',
                    color: '#333',
                    cursor: 'pointer',
                    fontSize: 14,
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#1D4E89';
                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                    e.currentTarget.style.color = '#1D4E89';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e0e0e0';
                    e.currentTarget.style.backgroundColor = '#fff';
                    e.currentTarget.style.color = '#333';
                  }}
                >
                  Audit
                </button>
              </>
            )}
          </div>
        </div>

        {/* Course Groups Display */}
        {courseGroups.length > 0 ? (
          <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
            {[...courseGroups].sort((a, b) => {
              const titleA = (a.title || '').toLowerCase();
              const titleB = (b.title || '').toLowerCase();
              return titleA.localeCompare(titleB);
            }).map(group => {
              const isExpanded = expandedCourseGroups.has(group.id);
              return (
                <div 
                  key={group.id}
                  style={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 8,
                    backgroundColor: '#fff',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    transition: 'all 0.2s ease',
                    overflow: 'hidden'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                    e.currentTarget.style.borderColor = '#1D4E89';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                    e.currentTarget.style.borderColor = '#e0e0e0';
                  }}
                >
                  {/* Title Header - Clickable to expand/collapse */}
                  <div
                    style={{
                      padding: 16,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      backgroundColor: isExpanded ? '#f8f9fa' : '#fff'
                    }}
                  >
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleCourseGroup(group.id);
                      }}
                      style={{
                        flex: 1,
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <h3 style={{ margin: 0, color: '#1D4E89', fontSize: '1rem', fontWeight: 600 }}>
                        {group.title}
                      </h3>
                      <span style={{ fontSize: '14px', color: '#666' }}>
                        {isExpanded ? '▼' : '▶'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginLeft: 12 }}>
                      
                    <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenRoster(group);
                        }}
                        style={{
                          padding: '6px',
                          borderRadius: 4,
                          border: '1px solid #17a2b8',
                          background: '#17a2b8',
                          color: '#fff',
                          cursor: 'pointer',
                          fontSize: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '32px',
                          height: '32px',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#138496';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = '#17a2b8';
                        }}
                        title="View Roster"
                      >
                        👤
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditCourseGroup(group);
                        }}
                        style={{
                          padding: '6px',
                          borderRadius: 4,
                          border: '1px solid #1D4E89',
                          background: '#1D4E89',
                          color: '#fff',
                          cursor: 'pointer',
                          fontSize: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '32px',
                          height: '32px',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#0d3d6e';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = '#1D4E89';
                        }}
                        title="Edit Course Group"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCloneCourseGroup(group);
                        }}
                        style={{
                          padding: '6px',
                          borderRadius: 4,
                          border: '1px solid #6c757d',
                          background: '#6c757d',
                          color: '#fff',
                          cursor: 'pointer',
                          fontSize: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '32px',
                          height: '32px',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#5a6268';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = '#6c757d';
                        }}
                        title="Clone Course Group"
                      >
                        📋
                      </button>
                    </div>
                  </div>
                  
                  {/* Expanded Content */}
                  {isExpanded && (
                    <div style={{ padding: '0 16px 16px 16px' }}>
                      <div style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
                        Created: {formatDate(group.createddate)}
                      </div>
                      <div style={{ marginBottom: 12 }}>
                        <strong>Courses ({group.courses_with_dates?.length || 0}):</strong>
                        <ul style={{ margin: 8, paddingLeft: 20 }}>
                          {group.courses_with_dates?.map(course => (
                            <li key={course.id} style={{ fontSize: 14, marginBottom: 4 }}>
                              {course.title}
                              {course.start_date && (
                                <span style={{ color: '#666', fontSize: 12 }}>
                                  {' '}({formatDate(course.start_date)} - {formatDate(course.end_date)})
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ padding: '32px', textAlign: 'center', color: '#888' }}>
            No course groups yet. Create one to get started.
          </div>
        )}
      </div>

      {/* Course Enrollments Container */}
      <div style={{
        padding: '24px',
        backgroundColor: '#fff',
        borderRadius: 8,
        border: '1px solid #e0e0e0',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
      }}>
        {/* Header - Clickable to expand/collapse */}
        <div 
          onClick={() => setCourseEnrollmentsExpanded(!courseEnrollmentsExpanded)}
          style={{
            marginBottom: courseEnrollmentsExpanded ? 20 : 0,
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: courseEnrollmentsExpanded ? 0 : '8px 0',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            if (!courseEnrollmentsExpanded) {
              e.currentTarget.style.backgroundColor = '#f8f9fa';
              e.currentTarget.style.paddingLeft = '8px';
              e.currentTarget.style.paddingRight = '8px';
              e.currentTarget.style.borderRadius = '6px';
            }
          }}
          onMouseLeave={(e) => {
            if (!courseEnrollmentsExpanded) {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.paddingLeft = '0';
              e.currentTarget.style.paddingRight = '0';
              e.currentTarget.style.borderRadius = '0';
            }
          }}
        >
          <h2 style={{ color: '#1D4E89', fontSize: '1.5rem', margin: 0 }}>Course Enrollments</h2>
          <span style={{ fontSize: '14px', color: '#666' }}>
            {courseEnrollmentsExpanded ? '▼' : '▶'}
          </span>
        </div>

        {/* Filters - Only show when expanded */}
        {courseEnrollmentsExpanded && (
          <div style={{ 
            display: 'flex', 
            gap: 16, 
            marginBottom: 24, 
            flexWrap: 'wrap',
            padding: '16px',
            backgroundColor: '#f8f9fa',
            borderRadius: 8,
            border: '1px solid #e0e0e0'
          }}>
          <input
            type="text"
            placeholder="Search by student or course name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ padding: '0.5rem 1rem', borderRadius: 6, border: '1px solid #ccc', minWidth: 300 }}
          />
          
          <select
            value={selectedCourse}
            onChange={(e) => setSelectedCourse(e.target.value)}
            style={{ padding: '0.5rem 1rem', borderRadius: 6, border: '1px solid #ccc', minWidth: 200 }}
          >
            <option value="">All Courses</option>
            {courses.map(course => (
              <option key={course.id} value={course.id}>
                {course.title}
              </option>
            ))}
          </select>

          <select
            value={selectedStudent}
            onChange={(e) => setSelectedStudent(e.target.value)}
            style={{ padding: '0.5rem 1rem', borderRadius: 6, border: '1px solid #ccc', minWidth: 200 }}
          >
            <option value="">All Students</option>
            {students.map(student => (
              <option key={student.enrollment_id} value={student.enrollment_id}>
                {student.first_name} {student.last_name}
              </option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ padding: '0.5rem 1rem', borderRadius: 6, border: '1px solid #ccc', minWidth: 150 }}
          >
            <option value="">All Statuses</option>
            <option value="enrolled">Enrolled</option>
            <option value="completed">Completed</option>
            <option value="dropped">Dropped</option>
            <option value="withdrawn">Withdrawn</option>
          </select>
        </div>
        )}

        {/* Course Enrollments Table - Only show when expanded */}
        {courseEnrollmentsExpanded && (
          <>
            {(() => {
              const courseGroupMap = new Map(courseGroups.map(g => [g.id, g.title]));
              const totalItems = filteredCourseEnrollments.length;
              const totalPages = Math.ceil(totalItems / itemsPerPage);
              const startIndex = (currentPage - 1) * itemsPerPage;
              const endIndex = startIndex + itemsPerPage;
              const paginatedEnrollments = filteredCourseEnrollments.slice(startIndex, endIndex);

              return (
                <>
                  {/* Summary */}
                  <div style={{ marginBottom: 16, padding: 16, backgroundColor: '#f8f9fa', borderRadius: 8 }}>
                    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                        <div>
                          <strong>Course Groups:</strong> {courseGroups.length}
                        </div>
                        <div>
                          <strong>Total Enrollments:</strong> {courseEnrollments.length}
                        </div>
                        <div>
                          <strong>Active:</strong> {courseEnrollments.filter(ce => ce.status === 'enrolled').length}
                        </div>
                        <div>
                          <strong>Completed:</strong> {courseEnrollments.filter(ce => ce.status === 'completed').length}
                        </div>
                        <div>
                          <strong>Dropped:</strong> {courseEnrollments.filter(ce => ce.status === 'dropped').length}
                        </div>
                        <div>
                          <strong>Withdrawn:</strong> {courseEnrollments.filter(ce => ce.status === 'withdrawn').length}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={handleSelectAllFiltered}
                          style={{
                            padding: '6px 12px',
                            borderRadius: 6,
                            border: '1px solid #1D4E89',
                            background: 'transparent',
                            color: '#1D4E89',
                            cursor: 'pointer',
                            fontSize: 14,
                            fontWeight: 500
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#e8eef8';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                          }}
                        >
                          Select all
                        </button>
                        <button
                          type="button"
                          onClick={handleDeselectAll}
                          style={{
                            padding: '6px 12px',
                            borderRadius: 6,
                            border: '1px solid #6b7280',
                            background: 'transparent',
                            color: '#6b7280',
                            cursor: 'pointer',
                            fontSize: 14,
                            fontWeight: 500
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#f3f4f6';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                          }}
                        >
                          Deselect all
                        </button>
                        {selectedEnrollments.size > 0 && (
                          <>
                            <button
                              onClick={handleBulkWithdraw}
                              style={{
                                padding: '8px 16px',
                                borderRadius: 6,
                                border: 'none',
                                background: '#eab308',
                                color: '#fff',
                                cursor: 'pointer',
                                fontSize: 14,
                                fontWeight: 600,
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.background = '#ca8a04';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background = '#eab308';
                              }}
                            >
                              Withdraw Selected ({selectedEnrollments.size})
                            </button>
                            {deletionsAllowed && (
                              <button
                                onClick={handleBulkDelete}
                                style={{
                                  padding: '8px 16px',
                                  borderRadius: 6,
                                  border: 'none',
                                  background: '#dc2626',
                                  color: '#fff',
                                  cursor: 'pointer',
                                  fontSize: 14,
                                  fontWeight: 600,
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.background = '#b91c1c';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.background = '#dc2626';
                                }}
                              >
                                Delete Selected ({selectedEnrollments.size})
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginBottom: 16,
                      padding: '12px 16px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: 8,
                      border: '1px solid #e0e0e0'
                    }}>
                      <div style={{ fontSize: 14, color: '#666' }}>
                        Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} enrollments
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <button
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                          style={{
                            padding: '6px 12px',
                            borderRadius: 6,
                            border: '1px solid #ccc',
                            background: currentPage === 1 ? '#f0f0f0' : '#fff',
                            color: currentPage === 1 ? '#999' : '#333',
                            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                            fontSize: 14,
                            fontWeight: 500
                          }}
                        >
                          Previous
                        </button>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                            // Show first page, last page, current page, and pages around current
                            if (
                              page === 1 ||
                              page === totalPages ||
                              (page >= currentPage - 1 && page <= currentPage + 1)
                            ) {
                              return (
                                <button
                                  key={page}
                                  onClick={() => setCurrentPage(page)}
                                  style={{
                                    padding: '6px 12px',
                                    borderRadius: 6,
                                    border: `1px solid ${currentPage === page ? '#1D4E89' : '#ccc'}`,
                                    background: currentPage === page ? '#1D4E89' : '#fff',
                                    color: currentPage === page ? '#fff' : '#333',
                                    cursor: 'pointer',
                                    fontSize: 14,
                                    fontWeight: currentPage === page ? 600 : 500
                                  }}
                                >
                                  {page}
                                </button>
                              );
                            } else if (
                              page === currentPage - 2 ||
                              page === currentPage + 2
                            ) {
                              return (
                                <span key={page} style={{ padding: '6px 4px', color: '#666' }}>
                                  ...
                                </span>
                              );
                            }
                            return null;
                          })}
                        </div>
                        <button
                          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                          disabled={currentPage === totalPages}
                          style={{
                            padding: '6px 12px',
                            borderRadius: 6,
                            border: '1px solid #ccc',
                            background: currentPage === totalPages ? '#f0f0f0' : '#fff',
                            color: currentPage === totalPages ? '#999' : '#333',
                            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                            fontSize: 14,
                            fontWeight: 500
                          }}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}

                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                      <thead>
                        <tr style={{ background: '#f8f9fa', color: '#1D4E89', fontWeight: 600 }}>
                          <th style={{ ...th, width: '40px', textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={(() => {
                                const selectable = paginatedEnrollments.filter(ce => ce.status !== 'withdrawn');
                                return selectable.length > 0 && selectable.every(ce => selectedEnrollments.has(ce.id));
                              })()}
                              onChange={(e) => handleSelectAll(e.target.checked)}
                              style={{
                                cursor: 'pointer',
                                width: '18px',
                                height: '18px'
                              }}
                            />
                          </th>
                          <th style={th}>Student</th>
                          <th style={th}>Course</th>
                          <th style={th}>Course Group</th>
                          <th style={th}>Start Date</th>
                          <th style={{ ...th, width: '40px', textAlign: 'center' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedEnrollments.length === 0 ? (
                          <tr>
                            <td colSpan={6} style={{ textAlign: 'center', color: '#888', padding: 32 }}>
                              No enrollments found.
                            </td>
                          </tr>
                        ) : paginatedEnrollments.map(ce => {
                          const isWithdrawn = ce.status === 'withdrawn';
                          const canSelect = !isWithdrawn;
                          return (
                            <tr key={ce.id} style={{ borderBottom: '1px solid #eee' }}>
                              <td style={{ ...td, textAlign: 'center', width: '40px' }}>
                                {canSelect ? (
                                  <input
                                    type="checkbox"
                                    checked={selectedEnrollments.has(ce.id)}
                                    onChange={() => handleToggleEnrollment(ce.id)}
                                    style={{
                                      cursor: 'pointer',
                                      width: '18px',
                                      height: '18px'
                                    }}
                                  />
                                ) : (
                                  <span style={{ color: '#ccc' }}>—</span>
                                )}
                              </td>
                              <td style={td}>
                                {ce.first_name} {ce.last_name}
                                <br />
                                <small style={{ color: '#666' }}>{ce.primary_email}</small>
                              </td>
                              <td style={td}>{ce.course_title}</td>
                              <td style={td}>{courseGroupMap.get(ce.coursegroup) || '—'}</td>
                              <td style={td}>
                                {formatDate(ce.start_date)}
                              </td>
                              <td style={{ ...td, textAlign: 'center', width: '40px' }}>
                                {isWithdrawn ? (
                                  <span
                                    title="Withdrawn"
                                    style={{
                                      color: '#eab308',
                                      fontSize: '18px',
                                      lineHeight: 1,
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                  >
                                    ×
                                  </span>
                                ) : deletionsAllowed ? (
                                  <button
                                    onClick={(e) => handleRemoveCourseEnrollment(ce.id, e)}
                                    title="Remove enrollment"
                                    style={{
                                      background: 'transparent',
                                      border: 'none',
                                      color: '#dc2626',
                                      cursor: 'pointer',
                                      fontSize: '18px',
                                      lineHeight: 1,
                                      padding: '4px',
                                      borderRadius: '4px',
                                      transition: 'all 0.2s ease',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      width: '24px',
                                      height: '24px'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.backgroundColor = '#fee2e2';
                                      e.target.style.color = '#b91c1c';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.backgroundColor = 'transparent';
                                      e.target.style.color = '#dc2626';
                                    }}
                                  >
                                    ×
                                  </button>
                                ) : (
                                  <button
                                    onClick={(e) => handleOpenWithdrawalModal(ce, e)}
                                    title="Withdraw from course"
                                    style={{
                                      background: 'transparent',
                                      border: 'none',
                                      color: '#eab308',
                                      cursor: 'pointer',
                                      fontSize: '14px',
                                      fontWeight: 500,
                                      padding: '4px 8px',
                                      borderRadius: '4px',
                                      transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.backgroundColor = '#fef9c3';
                                      e.target.style.color = '#ca8a04';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.backgroundColor = 'transparent';
                                      e.target.style.color = '#eab308';
                                    }}
                                  >
                                    Withdraw
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              );
            })()}
          </>
        )}
      </div>

      {/* Withdrawal Modal */}
      {showWithdrawalModal && enrollmentToWithdraw && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#111827' }}>
                Withdraw Student
              </h2>
              <button
                onClick={() => {
                  setShowWithdrawalModal(false);
                  setEnrollmentToWithdraw(null);
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '4px',
                  lineHeight: 1
                }}
                onMouseEnter={(e) => { e.target.style.color = '#374151'; }}
                onMouseLeave={(e) => { e.target.style.color = '#6b7280'; }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <p style={{ fontSize: '16px', color: '#374151', marginBottom: '16px' }}>
                Do you want to withdraw {enrollmentToWithdraw.first_name} {enrollmentToWithdraw.last_name} from {enrollmentToWithdraw.course_title}?
              </p>

              {enrollmentToWithdraw.coursegroup && (() => {
                const studentCourseGroup = courseGroups.find(g => g.id === enrollmentToWithdraw.coursegroup);
                if (studentCourseGroup && studentCourseGroup.setup && Array.isArray(studentCourseGroup.setup) && studentCourseGroup.setup.length > 1) {
                  const enrollmentsInGroup = courseEnrollments.filter(
                    c => c.enrollment === enrollmentToWithdraw.enrollment && c.coursegroup === enrollmentToWithdraw.coursegroup
                  );
                  if (enrollmentsInGroup.length > 1) {
                    return (
                      <div style={{ marginTop: '16px' }}>
                        <p style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#dc2626',
                          marginBottom: '12px'
                        }}>
                          NOTE: Student will be withdrawn from all {enrollmentsInGroup.length} courses in this group:
                        </p>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                          gap: '8px',
                          marginTop: '8px'
                        }}>
                          {enrollmentsInGroup.map(ce => (
                            <div
                              key={ce.id}
                              style={{
                                padding: '10px',
                                backgroundColor: ce.id === enrollmentToWithdraw.id ? '#eff6ff' : '#f9fafb',
                                borderRadius: '6px',
                                border: ce.id === enrollmentToWithdraw.id ? '1px solid #3b82f6' : '1px solid #e5e7eb'
                              }}
                            >
                              <div style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>
                                {ce.course_title}
                              </div>
                              {ce.id === enrollmentToWithdraw.id && (
                                <span style={{ fontSize: '10px', color: '#3b82f6', fontWeight: 600 }}>Current</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }
                }
                return null;
              })()}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={() => {
                  setShowWithdrawalModal(false);
                  setEnrollmentToWithdraw(null);
                }}
                style={{
                  padding: '10px 20px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  background: '#fff',
                  color: '#374151',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => { e.target.style.backgroundColor = '#f9fafb'; }}
                onMouseLeave={(e) => { e.target.style.backgroundColor = '#fff'; }}
              >
                Cancel
              </button>
              <button
                onClick={handleWithdrawEnrollment}
                style={{
                  padding: '10px 20px',
                  borderRadius: '6px',
                  border: 'none',
                  background: '#eab308',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => { e.target.style.backgroundColor = '#ca8a04'; }}
                onMouseLeave={(e) => { e.target.style.backgroundColor = '#eab308'; }}
              >
                Withdraw Student
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Course Group Creation Modal */}
      {showCourseGroupForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#fff',
            padding: '2rem',
            borderRadius: 8,
            maxWidth: 800,
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h2 style={{ marginTop: 0, marginBottom: 0 }}>
                {editingCourseGroupId ? 'Edit Course Group' : 'Create Course Group'}
              </h2>
              <button
                onClick={() => {
                  setShowCourseGroupForm(false);
                  setEditingCourseGroupId(null);
                  setCourseGroupFormData({
                    title: '',
                    courses: [],
                    setup: []
                  });
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  color: '#6b7280',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '4px',
                  transition: 'all 0.2s ease',
                  lineHeight: 1,
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#e5e7eb';
                  e.target.style.color = '#374151';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#6b7280';
                }}
                title="Close"
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleCreateCourseGroup}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Group Title:</label>
                <input
                  type="text"
                  value={courseGroupFormData.title}
                  onChange={(e) => setCourseGroupFormData({...courseGroupFormData, title: e.target.value})}
                  required
                  placeholder="e.g., Spring 2024 Core Courses"
                  style={{ width: '100%', padding: '0.5rem', borderRadius: 6, border: '1px solid #ccc' }}
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <label style={{ display: 'block', fontWeight: 600 }}>Select Courses:</label>
                  {courseGroupFormData.courses.length > 0 && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        onClick={handleLoadTemplateSequence}
                        style={{
                          padding: '0.4rem 0.8rem',
                          borderRadius: 6,
                          border: '1px solid #17a2b8',
                          background: '#17a2b8',
                          color: '#fff',
                          cursor: 'pointer',
                          fontSize: 14,
                          fontWeight: 500
                        }}
                        title="Load template sequence based on first course"
                      >
                        Load Template Sequence
                      </button>
                      <button
                        type="button"
                        onClick={handleApply2026StartDates}
                        style={{
                          padding: '0.4rem 0.8rem',
                          borderRadius: 6,
                          border: '1px solid #28a745',
                          background: '#28a745',
                          color: '#fff',
                          cursor: 'pointer',
                          fontSize: 14,
                          fontWeight: 500
                        }}
                        title="Overwrite start dates only: 2026-02-02, 2026-03-02, 2026-03-16, 2026-03-30, 2026-04-20, 2026-05-04"
                      >
                        Apply 2026 Start Dates
                      </button>
                    </div>
                  )}
                </div>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', 
                  gap: 12, 
                  marginBottom: 16,
                  maxHeight: '300px',
                  overflowY: 'auto',
                  padding: '8px',
                  border: '1px solid #e0e0e0',
                  borderRadius: 6,
                  backgroundColor: '#f8f9fa'
                }}>
                  {courses.map(course => {
                    const isSelected = courseGroupFormData.courses.includes(course.id);
                    return (
                      <div
                        key={course.id}
                        onClick={() => {
                          if (isSelected) {
                            removeCourseFromGroup(course.id);
                          } else {
                            addCourseToGroup(course.id);
                          }
                        }}
                        style={{
                          padding: '12px',
                          border: `2px solid ${isSelected ? '#1D4E89' : '#e0e0e0'}`,
                          borderRadius: 8,
                          backgroundColor: isSelected ? '#e3f2fd' : '#fff',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          position: 'relative'
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.target.style.borderColor = '#1D4E89';
                            e.target.style.backgroundColor = '#f0f8ff';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.target.style.borderColor = '#e0e0e0';
                            e.target.style.backgroundColor = '#fff';
                          }
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                          <div style={{
                            width: '20px',
                            height: '20px',
                            border: `2px solid ${isSelected ? '#1D4E89' : '#ccc'}`,
                            borderRadius: '4px',
                            backgroundColor: isSelected ? '#1D4E89' : 'transparent',
                            marginRight: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {isSelected && (
                              <span style={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>✓</span>
                            )}
                          </div>
                          <strong style={{ 
                            fontSize: '14px', 
                            color: isSelected ? '#1D4E89' : '#333',
                            flex: 1
                          }}>
                            {course.title}
                          </strong>
                        </div>
                        <div style={{ 
                          fontSize: '12px', 
                          color: '#666',
                          marginLeft: '28px'
                        }}>
                          Created: {course.createddate ? dateTimeToMonDDYYYYWithTime(course.createddate, false) : 'N/A'}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Selected Courses */}
                {courseGroupFormData.courses.length > 0 && (
                  <div style={{ border: '1px solid #e0e0e0', borderRadius: 6, padding: 16 }}>
                    <h4 style={{ marginTop: 0, marginBottom: 12 }}>
                      Selected Courses (Order: {courseGroupFormData.courses.map((_, idx) => idx + 1).join(' → ')}):
                    </h4>
                    {courseGroupFormData.courses.map((courseId, index) => {
                      const course = courses.find(c => c.id === courseId);
                      const setup = courseGroupFormData.setup.find(s => s.course_id === courseId);
                      const isFirst = index === 0;
                      const isLast = index === courseGroupFormData.courses.length - 1;
                      return (
                        <div key={courseId} style={{ 
                          border: '1px solid #e0e0e0', 
                          borderRadius: 6, 
                          padding: 12, 
                          marginBottom: 12,
                          backgroundColor: '#f8f9fa'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ 
                                fontWeight: 'bold', 
                                color: '#1D4E89',
                                fontSize: '14px',
                                minWidth: '24px',
                                textAlign: 'center'
                              }}>
                                {index + 1}.
                              </span>
                              <strong>{course?.title}</strong>
                            </div>
                            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                              <button
                                type="button"
                                onClick={() => moveCourseUp(index)}
                                disabled={isFirst}
                                title="Move up"
                                style={{
                                  padding: '4px 8px',
                                  borderRadius: 4,
                                  border: '1px solid #6c757d',
                                  background: isFirst ? '#ccc' : '#6c757d',
                                  color: '#fff',
                                  cursor: isFirst ? 'not-allowed' : 'pointer',
                                  fontSize: 12,
                                  opacity: isFirst ? 0.5 : 1
                                }}
                              >
                                ↑
                              </button>
                              <button
                                type="button"
                                onClick={() => moveCourseDown(index)}
                                disabled={isLast}
                                title="Move down"
                                style={{
                                  padding: '4px 8px',
                                  borderRadius: 4,
                                  border: '1px solid #6c757d',
                                  background: isLast ? '#ccc' : '#6c757d',
                                  color: '#fff',
                                  cursor: isLast ? 'not-allowed' : 'pointer',
                                  fontSize: 12,
                                  opacity: isLast ? 0.5 : 1
                                }}
                              >
                                ↓
                              </button>
                              <button
                                type="button"
                                onClick={() => removeCourseFromGroup(courseId)}
                                style={{
                                  padding: '4px 8px',
                                  borderRadius: 4,
                                  border: '1px solid #dc3545',
                                  background: '#dc3545',
                                  color: '#fff',
                                  cursor: 'pointer',
                                  fontSize: 12
                                }}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 12 }}>
                            <div style={{ flex: 1 }}>
                              <label style={{ display: 'block', marginBottom: 4, fontSize: 12, fontWeight: 600 }}>Start Date:</label>
                              <input
                                type="date"
                                value={setup?.start_date || ''}
                                onChange={(e) => updateCourseSetup(courseId, 'start_date', e.target.value)}
                                style={{ width: '100%', padding: '0.25rem', borderRadius: 4, border: '1px solid #ccc', fontSize: 12 }}
                              />
                            </div>
                            <div style={{ flex: 1 }}>
                              <label style={{ display: 'block', marginBottom: 4, fontSize: 12, fontWeight: 600 }}>End Date:</label>
                              <input
                                type="date"
                                value={setup?.end_date || ''}
                                onChange={(e) => updateCourseSetup(courseId, 'end_date', e.target.value)}
                                style={{ width: '100%', padding: '0.25rem', borderRadius: 4, border: '1px solid #ccc', fontSize: 12 }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowCourseGroupForm(false);
                    setEditingCourseGroupId(null);
                    setCourseGroupFormData({
                      title: '',
                      courses: [],
                      setup: []
                    });
                  }}
                  style={{
                    padding: '0.5rem 1rem',
                    borderRadius: 6,
                    border: '1px solid #ccc',
                    background: '#fff',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={courseGroupFormData.courses.length === 0}
                  style={{
                    padding: '0.5rem 1rem',
                    borderRadius: 6,
                    border: '1px solid #28a745',
                    background: courseGroupFormData.courses.length === 0 ? '#ccc' : '#28a745',
                    color: '#fff',
                    cursor: courseGroupFormData.courses.length === 0 ? 'not-allowed' : 'pointer'
                  }}
                >
                  {editingCourseGroupId ? 'Update Course Group' : 'Create Course Group'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Enrollment Modal */}
      {showBulkEnrollForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#fff',
            padding: '2rem',
            borderRadius: 8,
            maxWidth: 800,
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h2 style={{ marginTop: 0, marginBottom: 0 }}>Enroll Students</h2>
              <button
                onClick={() => {
                  setShowBulkEnrollForm(false);
                  setSelectedCourseGroup('');
                  setIsCourseGroupPreSelected(false);
                  setSelectedStudentsForBulk([]);
                  setBulkStudentSearch('');
                  setBulkCsvText('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  color: '#6b7280',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '4px',
                  transition: 'all 0.2s ease',
                  lineHeight: 1,
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#e5e7eb';
                  e.target.style.color = '#374151';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#6b7280';
                }}
                title="Close"
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleBulkEnroll}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Select Course Group:</label>
                <select
                  value={selectedCourseGroup}
                  onChange={(e) => setSelectedCourseGroup(e.target.value)}
                  required
                  disabled={isCourseGroupPreSelected}
                  style={{ 
                    width: '100%', 
                    padding: '0.5rem', 
                    borderRadius: 6, 
                    border: '1px solid #ccc',
                    backgroundColor: isCourseGroupPreSelected ? '#f0f0f0' : '#fff',
                    cursor: isCourseGroupPreSelected ? 'not-allowed' : 'pointer'
                  }}
                >
                  <option value="">Select a course group</option>
                  {[...courseGroups]
                    .sort((a, b) => {
                      const titleA = (a.title || '').toLowerCase();
                      const titleB = (b.title || '').toLowerCase();
                      return titleA.localeCompare(titleB); // Ascending order
                    })
                    .map(group => (
                      <option key={group.id} value={group.id}>
                        {group.title} ({group.courses_with_dates?.length || 0} courses)
                      </option>
                    ))}
                </select>
              </div>

              {/* Wheel Display */}
              {selectedCourseGroup && (() => {
                const selectedGroup = courseGroups.find(group => group.id === selectedCourseGroup);
                if (selectedGroup && selectedGroup.courses_with_dates && Array.isArray(selectedGroup.courses_with_dates) && selectedGroup.courses_with_dates.length > 0) {
                  return (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ 
                        fontSize: '12px', 
                        fontWeight: '600', 
                        color: '#374151', 
                        marginBottom: '8px'
                      }}>
                        Wheel ({selectedGroup.title})
                      </div>
                      <div style={{ 
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                        gap: '8px'
                      }}>
                        {selectedGroup.courses_with_dates.map((courseData, index) => {
                          const courseTitle = courseData.title || courseData.course_id;
                          
                          return (
                            <div
                              key={courseData.id || index}
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                padding: '10px',
                                backgroundColor: '#f9fafb',
                                borderRadius: '6px',
                                border: '1px solid #e5e7eb',
                                width: '100%',
                                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                              }}
                            >
                              <div style={{ 
                                fontSize: '11px', 
                                fontWeight: '600', 
                                color: '#374151',
                                marginBottom: '6px',
                                lineHeight: '1.3'
                              }}>
                                {courseTitle}
                              </div>
                              <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '4px' }}>
                                <div>Start: {courseData.start_date || 'N/A'}</div>
                                {courseData.end_date && (
                                  <div>End: {courseData.end_date}</div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              {selectedCourseGroup && (() => {
                const selectedGroup = courseGroups.find(g => g.id === selectedCourseGroup);
                const hasUnlocked = selectedGroup && groupHasUnlockedCourses(selectedGroup);
                return hasUnlocked ? (
                  <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: 6, color: '#856404' }}>
                    Enrollment is not allowed for one or more courses in this group because they are not locked for modifications. Please lock those courses first.
                  </div>
                ) : null;
              })()}
              {selectedCourseGroup && (
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Select Students:</label>
                  <input
                    type="text"
                    placeholder="Search by name or email..."
                    value={bulkStudentSearch}
                    onChange={(e) => setBulkStudentSearch(e.target.value)}
                    style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: 6, border: '1px solid #ccc', marginBottom: 12 }}
                  />
                  {/* CSV Email Input */}
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: 'block', marginBottom: 6, fontSize: 12, color: '#555' }}>Paste emails (CSV or newline separated):</label>
                    <textarea
                      value={bulkCsvText}
                      onChange={(e) => setBulkCsvText(e.target.value)}
                      placeholder="e.g. alice@example.com, bob@example.com\nor paste a column from a spreadsheet"
                      rows={3}
                      style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: 6, border: '1px solid #ccc', resize: 'vertical' }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                      <button
                        type="button"
                        onClick={handleSelectStudentsFromCsv}
                        style={{ padding: '0.4rem 0.8rem', borderRadius: 6, border: '1px solid #1D4E89', background: '#1D4E89', color: '#fff', cursor: 'pointer', fontSize: 14 }}
                      >
                        Select from CSV
                      </button>
                    </div>
                  </div>
                  {(() => {
                    const term = bulkStudentSearch.trim().toLowerCase();
                    const filtered = term
                      ? students.filter(s => `${s.first_name || ''} ${s.last_name || ''}`.toLowerCase().includes(term) || (s.email || '').toLowerCase().includes(term))
                      : students;
                    return (
                  <div style={{ 
                    border: '1px solid #e0e0e0', 
                    borderRadius: 6, 
                    padding: 16, 
                    maxHeight: 300, 
                    overflow: 'auto',
                    backgroundColor: '#f8f9fa'
                  }}>
                    {filtered.map(student => (
                      <div key={student.enrollment_id} style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        padding: '8px 0',
                        borderBottom: '1px solid #e0e0e0'
                      }}>
                        <input
                          type="checkbox"
                          checked={selectedStudentsForBulk.includes(student.enrollment_id)}
                          onChange={() => toggleStudentSelection(student.enrollment_id)}
                          style={{ marginRight: 12 }}
                        />
                        <div>
                          <strong>{student.first_name} {student.last_name}</strong>
                          <br />
                          <small style={{ color: '#666' }}>{student.email}</small>
                        </div>
                      </div>
                    ))}
                  </div>
                  );
                  })()}
                  <div style={{ marginTop: 8, fontSize: 14, color: '#666' }}>
                    {selectedStudentsForBulk.length} student(s) selected
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowBulkEnrollForm(false);
                    setSelectedCourseGroup('');
                    setIsCourseGroupPreSelected(false);
                    setSelectedStudentsForBulk([]);
                    setBulkStudentSearch('');
                    setBulkCsvText('');
                  }}
                  style={{
                    padding: '0.5rem 1rem',
                    borderRadius: 6,
                    border: '1px solid #ccc',
                    background: '#fff',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                {(() => {
                  const selectedGroup = courseGroups.find(g => g.id === selectedCourseGroup);
                  const hasUnlocked = selectedGroup && groupHasUnlockedCourses(selectedGroup);
                  const isDisabled = selectedStudentsForBulk.length === 0 || hasUnlocked;
                  return (
                    <button
                      type="submit"
                      disabled={isDisabled}
                      style={{
                        padding: '0.5rem 1rem',
                        borderRadius: 6,
                        border: '1px solid #17a2b8',
                        background: isDisabled ? '#ccc' : '#17a2b8',
                        color: '#fff',
                        cursor: isDisabled ? 'not-allowed' : 'pointer'
                      }}
                    >
                      Enroll {selectedStudentsForBulk.length} Student(s)
                    </button>
                  );
                })()}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Roster Modal */}
      {showRosterModal && selectedRosterCourseGroup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#fff',
            padding: '2rem',
            borderRadius: 8,
            maxWidth: 1000,
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h2 style={{ marginTop: 0, marginBottom: 0 }}>
                Roster: {selectedRosterCourseGroup.title}
              </h2>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button
                  onClick={() => {
                    setShowRosterModal(false);
                    setSelectedRosterCourseGroup(null);
                    setExpandedEnrollments(new Set());
                    setRosterMatchMode(false);
                    setRosterSelectedMissing(new Set());
                    // Open Enroll Students popup with course group pre-selected
                    setSelectedCourseGroup(selectedRosterCourseGroup.id);
                    setIsCourseGroupPreSelected(true);
                    setShowBulkEnrollForm(true);
                  }}
                  style={{
                    padding: '6px 12px',
                    borderRadius: 6,
                    border: '1px solid #17a2b8',
                    background: '#17a2b8',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 500,
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#138496';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#17a2b8';
                  }}
                  title="Enroll Students"
                >
                  Enroll
                </button>
                <button
                  onClick={() => {
                    setShowRosterModal(false);
                    setSelectedRosterCourseGroup(null);
                    setExpandedEnrollments(new Set());
                    setRosterMatchMode(false);
                    setRosterSelectedMissing(new Set());
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    color: '#6b7280',
                    cursor: 'pointer',
                    padding: '4px',
                    borderRadius: '4px',
                    transition: 'all 0.2s ease',
                    lineHeight: 1,
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#e5e7eb';
                    e.target.style.color = '#374151';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#6b7280';
                  }}
                  title="Close"
                >
                  ×
                </button>
              </div>
            </div>

            {(() => {
              const groupEnrollments = getEnrollmentsByCourseGroup(selectedRosterCourseGroup.id);
              
              if (groupEnrollments.length === 0) {
                return (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                    No enrollments found for this course group.
                  </div>
                );
              }

              // Group enrollments by enrollment ID
              const groupedEnrollments = {};
              groupEnrollments.forEach(ce => {
                const enrollmentId = ce.enrollment;
                if (!groupedEnrollments[enrollmentId]) {
                  groupedEnrollments[enrollmentId] = [];
                }
                groupedEnrollments[enrollmentId].push(ce);
              });

              // Sort each group's enrollments by cohort date (earliest first)
              Object.keys(groupedEnrollments).forEach(enrollmentId => {
                groupedEnrollments[enrollmentId].sort((a, b) => {
                  const dateA = a.start_date ? new Date(a.start_date) : new Date(0);
                  const dateB = b.start_date ? new Date(b.start_date) : new Date(0);
                  return dateA - dateB; // Ascending order (earliest first)
                });
              });

              const enrollmentGroups = Object.values(groupedEnrollments);
              const totalEnrollments = groupEnrollments.length;

              // Course IDs that belong to the selected course group
              const groupCourseIds = selectedRosterCourseGroup.courses && selectedRosterCourseGroup.courses.length
                ? new Set(selectedRosterCourseGroup.courses)
                : new Set((selectedRosterCourseGroup.courses_with_dates || []).map(c => c.id));
              const courseBelongsToGroup = (ce) => groupCourseIds.has(ce.course);

              const courseIdToTitle = new Map();
              courses.forEach(c => courseIdToTitle.set(c.id, c.title || c.id));

              // Enrollment IDs that have missing courses in this roster's course group
              const enrollmentIdsWithMissing = enrollmentGroups
                .filter(eg => {
                  const enrolledCourseIds = new Set(eg.map(ce => ce.course));
                  const missing = [...groupCourseIds].filter(cid => !enrolledCourseIds.has(cid));
                  return missing.length > 0;
                })
                .map(eg => eg[0].enrollment);
              const hasAnyMissing = enrollmentIdsWithMissing.length > 0;
              const allSelected = hasAnyMissing && enrollmentIdsWithMissing.every(id => rosterSelectedMissing.has(id));

              return (
                <>
                  <div style={{ marginBottom: 16, fontSize: 14, color: '#666', fontWeight: 500 }}>
                    Total: {totalEnrollments} enrollment(s) | {enrollmentGroups.length} student(s)
                  </div>
                  {!rosterMatchMode ? (
                    hasAnyMissing && (
                      <div style={{ marginBottom: 16 }}>
                        <button
                          type="button"
                          onClick={() => {
                            setRosterMatchMode(true);
                            setRosterSelectedMissing(new Set(enrollmentIdsWithMissing));
                            setExpandedEnrollments(prev => new Set([...prev, ...enrollmentIdsWithMissing]));
                          }}
                          style={{
                            padding: '8px 16px',
                            borderRadius: 6,
                            border: '1px solid #1D4E89',
                            background: '#1D4E89',
                            color: '#fff',
                            cursor: 'pointer',
                            fontSize: 14,
                            fontWeight: 600
                          }}
                        >
                          Match Course Group
                        </button>
                        <span style={{ marginLeft: 12, fontSize: 13, color: '#666' }}>
                          Select and enroll {enrollmentIdsWithMissing.length} student(s) missing courses
                        </span>
                      </div>
                    )
                  ) : (
                    <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        onClick={() => {
                          setRosterMatchMode(false);
                          setRosterSelectedMissing(new Set());
                        }}
                        style={{
                          padding: '6px 12px',
                          borderRadius: 6,
                          border: '1px solid #6b7280',
                          background: '#fff',
                          color: '#374151',
                          cursor: 'pointer',
                          fontSize: 14
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (allSelected) {
                            setRosterSelectedMissing(new Set());
                          } else {
                            setRosterSelectedMissing(new Set(enrollmentIdsWithMissing));
                          }
                        }}
                        style={{
                          padding: '6px 12px',
                          borderRadius: 6,
                          border: '1px solid #1D4E89',
                          background: '#fff',
                          color: '#1D4E89',
                          cursor: 'pointer',
                          fontSize: 14
                        }}
                      >
                        {allSelected ? 'Deselect all' : 'Select all'}
                      </button>
                      <button
                        type="button"
                        onClick={handleRosterBulkEnroll}
                        disabled={rosterSelectedMissing.size === 0}
                        style={{
                          padding: '6px 12px',
                          borderRadius: 6,
                          border: '1px solid #28a745',
                          background: rosterSelectedMissing.size === 0 ? '#ccc' : '#28a745',
                          color: '#fff',
                          cursor: rosterSelectedMissing.size === 0 ? 'not-allowed' : 'pointer',
                          fontSize: 14,
                          fontWeight: 600
                        }}
                      >
                        Enroll Missing Course(s) ({rosterSelectedMissing.size} selected)
                      </button>
                    </div>
                  )}
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                      <thead>
                        <tr style={{ background: '#f8f9fa', color: '#1D4E89', fontWeight: 600 }}>
                          {rosterMatchMode && (
                            <th style={{ ...th, width: '36px', minWidth: '36px', padding: '8px 4px' }}>
                              <input
                                type="checkbox"
                                checked={allSelected}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setRosterSelectedMissing(new Set(enrollmentIdsWithMissing));
                                  } else {
                                    setRosterSelectedMissing(new Set());
                                  }
                                }}
                                title={allSelected ? 'Deselect all' : 'Select all'}
                              />
                            </th>
                          )}
                          <th style={{ ...th, width: '24px', minWidth: '24px', maxWidth: '24px', padding: '8px 4px' }}>#</th>
                          <th style={th}>Student</th>
                          <th style={th}>Course</th>
                          <th style={th}>Cohort Date</th>
                          <th style={th}>Added at</th>
                          <th style={{ ...th, width: '40px', textAlign: 'center' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {enrollmentGroups.map((enrollments, groupIndex) => {
                          const enrollmentId = enrollments[0].enrollment;
                          const isExpanded = expandedEnrollments.has(enrollmentId);
                          const student = enrollments[0]; // Use first enrollment for student info
                          const hasMultipleCourses = enrollments.length > 1;

                          const enrolledCourseIds = new Set(enrollments.map(ce => ce.course));
                          const missingCourseIds = [...groupCourseIds].filter(cid => !enrolledCourseIds.has(cid));
                          const showMissingCourses = missingCourseIds.length > 0 && (isExpanded || !hasMultipleCourses);

                          const hasMissing = missingCourseIds.length > 0;

                          return (
                            <React.Fragment key={enrollmentId}>
                              {/* Main student row */}
                              <tr 
                                style={{ 
                                  borderBottom: '1px solid #eee',
                                  backgroundColor: isExpanded && hasMultipleCourses ? '#f8f9fa' : '#fff',
                                  cursor: hasMultipleCourses ? 'pointer' : 'default'
                                }}
                                onClick={hasMultipleCourses ? () => toggleEnrollmentExpansion(enrollmentId) : undefined}
                              >
                                {rosterMatchMode && (
                                  <td style={{ ...td, width: '36px', minWidth: '36px', padding: '8px 4px', verticalAlign: 'top' }} onClick={(e) => e.stopPropagation()}>
                                    {hasMissing ? (
                                      <input
                                        type="checkbox"
                                        checked={rosterSelectedMissing.has(enrollmentId)}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          setRosterSelectedMissing(prev => {
                                            const next = new Set(prev);
                                            if (e.target.checked) next.add(enrollmentId);
                                            else next.delete(enrollmentId);
                                            return next;
                                          });
                                        }}
                                        title="This course is missing for the student"
                                      />
                                    ) : null}
                                  </td>
                                )}
                                <td style={{ 
                                  textAlign: 'left', 
                                  fontWeight: '500', 
                                  color: '#6b7280',
                                  width: '24px',
                                  minWidth: '24px',
                                  maxWidth: '24px',
                                  padding: '8px 4px',
                                  verticalAlign: 'top'
                                }}>
                                  <div style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '20px',
                                    height: '20px',
                                    backgroundColor: '#f3f4f6',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    color: '#6b7280'
                                  }}>
                                    {groupIndex + 1}
                                  </div>
                                </td>
                                <td style={td}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {hasMultipleCourses && (
                                      <span style={{ fontSize: '12px', color: '#666' }}>
                                        {isExpanded ? '▼' : '▶'}
                                      </span>
                                    )}
                                    <div>
                                      {student.first_name} {student.last_name}
                                      <br />
                                      <small style={{ color: '#666' }}>{student.primary_email}</small>
                                    </div>
                                  </div>
                                </td>
                                <td style={{
                                  ...td,
                                  ...(!hasMultipleCourses && !courseBelongsToGroup(enrollments[0]) && { backgroundColor: '#fee2e2', color: '#dc2626' })
                                }}>
                                  {hasMultipleCourses ? (
                                    <span style={{ color: '#666', fontStyle: 'italic' }}>
                                      {enrollments.length} course(s)
                                    </span>
                                  ) : (
                                    enrollments[0].course_title
                                  )}
                                </td>
                                <td style={td}>
                                  {!hasMultipleCourses && (enrollments[0].start_date ? (
                                    <span style={{ color: '#333' }}>
                                      {formatDate(enrollments[0].start_date)}
                                    </span>
                                  ) : !hasMultipleCourses ? (
                                    <span style={{ color: '#888' }}>N/A</span>
                                  ) : null)}
                                </td>
                                <td style={td}>
                                  {!hasMultipleCourses && (enrollments[0].createddate ? (
                                    <span style={{ color: '#333' }}>
                                      {formatDateWithTime(enrollments[0].createddate)}
                                    </span>
                                  ) : !hasMultipleCourses ? (
                                    <span style={{ color: '#888' }}>N/A</span>
                                  ) : null)}
                                </td>
                                <td style={{ ...td, textAlign: 'center', width: '40px' }}>
                                  {!hasMultipleCourses ? (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveCourseEnrollment(enrollments[0].id, e);
                                      }}
                                      title="Remove enrollment"
                                      style={{
                                        background: 'transparent',
                                        border: 'none',
                                        color: '#dc2626',
                                        cursor: 'pointer',
                                        fontSize: '18px',
                                        lineHeight: 1,
                                        padding: '4px',
                                        borderRadius: '4px',
                                        transition: 'all 0.2s ease',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '24px',
                                        height: '24px'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.target.style.backgroundColor = '#fee2e2';
                                        e.target.style.color = '#b91c1c';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.target.style.backgroundColor = 'transparent';
                                        e.target.style.color = '#dc2626';
                                      }}
                                    >
                                      ×
                                    </button>
                                  ) : null}
                                </td>
                              </tr>
                              
                              {/* Expanded course rows - all courses including first */}
                              {isExpanded && hasMultipleCourses && enrollments.map((ce, courseIndex) => (
                                <tr 
                                  key={`${enrollmentId}-${ce.id}`}
                                  onClick={(e) => e.stopPropagation()}
                                  style={{ 
                                    borderBottom: '1px solid #eee',
                                    backgroundColor: '#f8f9fa'
                                  }}
                                >
                                  {rosterMatchMode && <td style={{ ...td, width: '36px', minWidth: '36px', padding: '8px 4px' }}></td>}
                                  <td style={{ 
                                    textAlign: 'left', 
                                    width: '24px',
                                    minWidth: '24px',
                                    maxWidth: '24px',
                                    padding: '8px 4px',
                                    verticalAlign: 'top'
                                  }}></td>
                                  <td style={td}></td>
                                  <td style={{
                                    ...td,
                                    ...(!courseBelongsToGroup(ce) && { backgroundColor: '#fee2e2', color: '#dc2626' })
                                  }}>{ce.course_title}</td>
                                  <td style={td}>
                                    {ce.start_date ? (
                                      <span style={{ color: '#333' }}>
                                        {formatDate(ce.start_date)}
                                      </span>
                                    ) : (
                                      <span style={{ color: '#888' }}>N/A</span>
                                    )}
                                  </td>
                                  <td style={td}>
                                    {ce.createddate ? (
                                      <span style={{ color: '#333' }}>
                                        {formatDateWithTime(ce.createddate)}
                                      </span>
                                    ) : (
                                      <span style={{ color: '#888' }}>N/A</span>
                                    )}
                                  </td>
                                  <td style={{ ...td, textAlign: 'center', width: '40px' }}>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveCourseEnrollment(ce.id, e);
                                      }}
                                      title="Remove enrollment"
                                      style={{
                                        background: 'transparent',
                                        border: 'none',
                                        color: '#dc2626',
                                        cursor: 'pointer',
                                        fontSize: '18px',
                                        lineHeight: 1,
                                        padding: '4px',
                                        borderRadius: '4px',
                                        transition: 'all 0.2s ease',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '24px',
                                        height: '24px'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.target.style.backgroundColor = '#fee2e2';
                                        e.target.style.color = '#b91c1c';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.target.style.backgroundColor = 'transparent';
                                        e.target.style.color = '#dc2626';
                                      }}
                                    >
                                      ×
                                    </button>
                                  </td>
                                </tr>
                              ))}

                              {/* Missing course rows - courses in group that student is not enrolled in */}
                              {showMissingCourses && missingCourseIds.map((courseId) => (
                                <tr
                                  key={`${enrollmentId}-missing-${courseId}`}
                                  onClick={(e) => e.stopPropagation()}
                                  style={{
                                    borderBottom: '1px solid #eee',
                                    backgroundColor: '#fef2f2'
                                  }}
                                >
                                  {rosterMatchMode && <td style={{ ...td, width: '36px', minWidth: '36px', padding: '8px 4px' }}></td>}
                                  <td style={{ ...td, width: '24px', minWidth: '24px', maxWidth: '24px', padding: '8px 4px' }}></td>
                                  <td style={td}></td>
                                  <td
                                    style={{
                                      ...td,
                                      border: '3px solid #dc2626',
                                      backgroundColor: '#fee2e2',
                                      color: '#b91c1c',
                                      fontWeight: 500
                                    }}
                                  >
                                    {courseIdToTitle.get(courseId) || courseId}
                                    <div style={{ fontSize: '0.75rem', marginTop: 4, fontStyle: 'italic' }}>
                                      This course is missing for the student
                                    </div>
                                  </td>
                                  <td style={td}></td>
                                  <td style={td}></td>
                                  <td style={{ ...td, textAlign: 'center', width: '40px' }}></td>
                                </tr>
                              ))}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Comparator Modal */}
      {showComparatorModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#fff',
            padding: '2rem',
            borderRadius: 8,
            maxWidth: 1000,
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            {/* Close button */}
            <button
              onClick={handleCloseComparatorModal}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: 'transparent',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#666',
                lineHeight: 1,
                padding: 0,
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f0f0f0';
                e.target.style.color = '#333';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#666';
              }}
            >
              ×
            </button>

            <h2 style={{ color: '#1D4E89', fontSize: '1.5rem', marginBottom: '1.5rem', marginTop: 0 }}>
              CSV Comparator
            </h2>

            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ marginBottom: '0.75rem', display: 'flex', flexWrap: 'wrap', gap: '1.25rem', alignItems: 'flex-start' }}>
                <fieldset style={{ margin: 0, padding: '0.5rem 0.75rem', border: '1px solid #ddd', borderRadius: 6, minWidth: 200 }}>
                  <legend style={{ fontSize: 12, fontWeight: 600, color: '#333' }}>CSV columns</legend>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, cursor: 'pointer', fontSize: 13 }}>
                    <input
                      type="radio"
                      name="comparatorSource"
                      checked={comparatorSourceMode === 'main_course_raw'}
                      onChange={() => setComparatorSourceMode('main_course_raw')}
                    />
                    Main sheet (course-raw)
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                    <input
                      type="radio"
                      name="comparatorSource"
                      checked={comparatorSourceMode === 'legacy_title'}
                      onChange={() => setComparatorSourceMode('legacy_title')}
                    />
                    Legacy (course = group title)
                  </label>
                </fieldset>
                <fieldset style={{ margin: 0, padding: '0.5rem 0.75rem', border: '1px solid #ddd', borderRadius: 6, minWidth: 200 }}>
                  <legend style={{ fontSize: 12, fontWeight: 600, color: '#333' }}>Enrollments counted</legend>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, cursor: 'pointer', fontSize: 13 }}>
                    <input
                      type="radio"
                      name="comparatorSemester"
                      checked={comparatorSemesterMode === 's2_only'}
                      onChange={() => setComparatorSemesterMode('s2_only')}
                    />
                    S2 comparator (ignore clear S1 groups)
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                    <input
                      type="radio"
                      name="comparatorSemester"
                      checked={comparatorSemesterMode === 'all'}
                      onChange={() => setComparatorSemesterMode('all')}
                    />
                    All course groups
                  </label>
                </fieldset>
                {comparatorSourceMode === 'main_course_raw' && (
                  <fieldset style={{ margin: 0, padding: '0.5rem 0.75rem', border: '1px solid #ddd', borderRadius: 6, minWidth: 180 }}>
                    <legend style={{ fontSize: 12, fontWeight: 600, color: '#333' }}>Code from course-raw</legend>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, cursor: 'pointer', fontSize: 13 }}>
                      <input
                        type="radio"
                        name="comparatorTaxSem"
                        checked={comparatorTaxonomySemester === 'S2'}
                        onChange={() => setComparatorTaxonomySemester('S2')}
                      />
                      Semester 2 (.S2.)
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                      <input
                        type="radio"
                        name="comparatorTaxSem"
                        checked={comparatorTaxonomySemester === 'S1'}
                        onChange={() => setComparatorTaxonomySemester('S1')}
                      />
                      Semester 1 (.S1.)
                    </label>
                  </fieldset>
                )}
              </div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#333' }}>
                {comparatorSourceMode === 'main_course_raw'
                  ? 'Paste Main export: required columns "email" and "course-raw"; optional "School Level" or "gradeLevel"; optional "status".'
                  : 'Paste CSV with "email" and "course" (exact course group title); optional "status".'}
              </label>
              <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '0.5rem' }}>
                <strong>Note:</strong> If &quot;status&quot; is inactive / 0 / false, the row is checked for full unenrollment (all enrollments).
                {comparatorSourceMode === 'main_course_raw' && (
                  <>
                    {' '}
                    Course-raw maps to taxonomy codes like the Apps Script helper. With S2 comparator: if every enrollment that has a <strong>course group</strong> is in an S1-titled group, the row is <strong>Ignored</strong> (ungrouped course rows do not block that). No on-file or no <em>active</em> enrollment counts as <strong>Not found</strong> (treat as needs S2).
                  </>
                )}
              </div>
              <textarea
                value={comparatorCsvText}
                onChange={(e) => setComparatorCsvText(e.target.value)}
                placeholder={comparatorSourceMode === 'main_course_raw'
                  ? 'lastName,firstName,...,email,...,course-raw,...,School Level,...'
                  : 'email,course\nstudent@example.com,Course Group Title'}
                style={{
                  width: '100%',
                  minHeight: '200px',
                  padding: '12px',
                  border: '1px solid #ccc',
                  borderRadius: 6,
                  fontSize: 14,
                  fontFamily: 'monospace',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginBottom: '1.5rem' }}>
              <button
                onClick={handleCloseComparatorModal}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: 6,
                  border: '1px solid #ccc',
                  background: '#fff',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCompareCsv}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: 6,
                  border: '1px solid #1D4E89',
                  background: '#1D4E89',
                  color: '#fff',
                  cursor: 'pointer'
                }}
              >
                Compare
              </button>
            </div>

            {/* Results */}
            {comparatorResults && (
              <div style={{ marginTop: '2rem', borderTop: '2px solid #e0e0e0', paddingTop: '1.5rem' }}>
                <h3 style={{ color: '#1D4E89', fontSize: '1.2rem', marginBottom: '1rem', marginTop: 0 }}>
                  Comparison Results
                </h3>
                
                {/* Summary */}
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap',
                  gap: 24, 
                  marginBottom: '1.5rem',
                  padding: '12px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: 6
                }}>
                  <div 
                    onClick={() => setComparatorFilter(null)}
                    style={{ 
                      cursor: 'pointer',
                      padding: '4px 8px',
                      borderRadius: 4,
                      backgroundColor: comparatorFilter === null ? '#e0e0e0' : 'transparent',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (comparatorFilter !== null) {
                        e.currentTarget.style.backgroundColor = '#e8e8e8';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (comparatorFilter !== null) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <strong>Total:</strong> {comparatorResults.length}
                  </div>
                  <div 
                    onClick={() => setComparatorFilter(comparatorFilter === 'match' ? null : 'match')}
                    style={{ 
                      color: '#28a745',
                      cursor: 'pointer',
                      padding: '4px 8px',
                      borderRadius: 4,
                      backgroundColor: comparatorFilter === 'match' ? '#d4edda' : 'transparent',
                      transition: 'all 0.2s ease',
                      fontWeight: comparatorFilter === 'match' ? 700 : 400
                    }}
                    onMouseEnter={(e) => {
                      if (comparatorFilter !== 'match') {
                        e.currentTarget.style.backgroundColor = '#e8f5e9';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (comparatorFilter !== 'match') {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <strong>Matches:</strong> {comparatorResults.filter(r => r.status === 'match').length}
                  </div>
                  <div 
                    onClick={() => setComparatorFilter(comparatorFilter === 'mismatch' ? null : 'mismatch')}
                    style={{ 
                      color: '#dc3545',
                      cursor: 'pointer',
                      padding: '4px 8px',
                      borderRadius: 4,
                      backgroundColor: comparatorFilter === 'mismatch' ? '#fff3cd' : 'transparent',
                      transition: 'all 0.2s ease',
                      fontWeight: comparatorFilter === 'mismatch' ? 700 : 400
                    }}
                    onMouseEnter={(e) => {
                      if (comparatorFilter !== 'mismatch') {
                        e.currentTarget.style.backgroundColor = '#fff9e6';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (comparatorFilter !== 'mismatch') {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <strong>Mismatches:</strong> {comparatorResults.filter(r => r.status === 'mismatch').length}
                  </div>
                  <div 
                    onClick={() => setComparatorFilter(comparatorFilter === 'not_found' ? null : 'not_found')}
                    style={{ 
                      color: '#6c757d',
                      cursor: 'pointer',
                      padding: '4px 8px',
                      borderRadius: 4,
                      backgroundColor: comparatorFilter === 'not_found' ? '#f8d7da' : 'transparent',
                      transition: 'all 0.2s ease',
                      fontWeight: comparatorFilter === 'not_found' ? 700 : 400
                    }}
                    onMouseEnter={(e) => {
                      if (comparatorFilter !== 'not_found') {
                        e.currentTarget.style.backgroundColor = '#fce4ec';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (comparatorFilter !== 'not_found') {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <strong>Not Found:</strong> {comparatorResults.filter(r => r.status === 'not_found').length}
                  </div>
                  <div
                    onClick={() => setComparatorFilter(comparatorFilter === 'ignored' ? null : 'ignored')}
                    style={{
                      color: '#5a6268',
                      cursor: 'pointer',
                      padding: '4px 8px',
                      borderRadius: 4,
                      backgroundColor: comparatorFilter === 'ignored' ? '#e2e3e5' : 'transparent',
                      transition: 'all 0.2s ease',
                      fontWeight: comparatorFilter === 'ignored' ? 700 : 400
                    }}
                    onMouseEnter={(e) => {
                      if (comparatorFilter !== 'ignored') {
                        e.currentTarget.style.backgroundColor = '#ececed';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (comparatorFilter !== 'ignored') {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <strong>Ignored (S1 only):</strong>{' '}
                    {comparatorResults.filter(r => r.status === 'ignored').length}
                  </div>
                </div>

                {/* Results Table */}
                <div style={{ overflowX: 'auto', maxHeight: '400px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
                    <thead>
                      <tr style={{ background: '#f8f9fa', color: '#1D4E89', fontWeight: 600 }}>
                        <th style={{ ...th, width: '60px' }}>Row</th>
                        <th style={th}>Email</th>
                        <th 
                          style={{ 
                            ...th, 
                            cursor: 'pointer',
                            userSelect: 'none',
                            backgroundColor: comparatorSortColumn === 'course' ? '#e9ecef' : undefined
                          }}
                          onClick={() => handleComparatorSort('course')}
                          onMouseEnter={(e) => {
                            if (comparatorSortColumn !== 'course') {
                              e.currentTarget.style.backgroundColor = '#f0f0f0';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (comparatorSortColumn !== 'course') {
                              e.currentTarget.style.backgroundColor = undefined;
                            }
                          }}
                        >
                          Expected Course
                          {comparatorSortColumn === 'course' && (
                            <span style={{ marginLeft: '4px', fontSize: '12px' }}>
                              {comparatorSortOrder === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </th>
                        <th style={th}>Status</th>
                        <th style={th}>Message</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortComparatorResults(
                        comparatorResults.filter(result => comparatorFilter === null || result.status === comparatorFilter)
                      )
                        .map((result, index) => {
                        const statusColors = {
                          match: { bg: '#d4edda', text: '#155724', border: '#c3e6cb' },
                          ignored: { bg: '#e9ecef', text: '#495057', border: '#ced4da' },
                          mismatch: { bg: '#fff3cd', text: '#856404', border: '#ffeaa7' },
                          not_found: { bg: '#f8d7da', text: '#721c24', border: '#f5c6cb' }
                        };
                        const colors = statusColors[result.status] || statusColors.not_found;
                        
                        // Override colors for inactive students who should be unenrolled
                        const inactiveColors = result.shouldUnenroll 
                          ? { bg: '#f8d7da', text: '#721c24', border: '#f5c6cb' }
                          : colors;
                        const displayColors = result.shouldUnenroll ? inactiveColors : colors;

                        return (
                          <tr key={index} style={{ borderBottom: '1px solid #eee' }}>
                            <td style={td}>{result.rowNumber}</td>
                            <td style={td}>{result.email}</td>
                            <td style={td}>
                              {result.isInactive ? (
                                <span style={{ fontStyle: 'italic', color: '#999' }}>
                                  {result.course || 'N/A (inactive)'}
                                </span>
                              ) : result.courseRaw ? (
                                <div>
                                  <div style={{ fontWeight: 600 }}>{result.expectedCode || result.course || '—'}</div>
                                  <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>course-raw: {result.courseRaw}</div>
                                </div>
                              ) : (
                                result.course
                              )}
                            </td>
                            <td style={td}>
                              <span style={{
                                padding: '4px 8px',
                                borderRadius: 4,
                                fontSize: 12,
                                fontWeight: 600,
                                backgroundColor: displayColors.bg,
                                color: displayColors.text,
                                border: `1px solid ${displayColors.border}`
                              }}>
                                {result.shouldUnenroll ? '⚠ Unenroll' :
                                 result.status === 'match' ? '✓ Match' :
                                 result.status === 'ignored' ? '— Ignored' :
                                 result.status === 'mismatch' ? '✗ Mismatch' :
                                 '? Not Found'}
                              </span>
                            </td>
                            <td style={td}>
                              <small style={{ color: result.shouldUnenroll ? '#721c24' : '#666', fontWeight: result.shouldUnenroll ? 600 : 400 }}>
                                {result.message}
                              </small>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Audit Modal */}
      {showAuditModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#fff',
            padding: '2rem',
            borderRadius: 8,
            maxWidth: 1000,
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            <button
              onClick={() => setShowAuditModal(false)}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: 'transparent',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#666',
                lineHeight: 1,
                padding: 0,
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f0f0f0';
                e.target.style.color = '#333';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#666';
              }}
            >
              ×
            </button>

            <h2 style={{ color: '#1D4E89', fontSize: '1.5rem', marginBottom: '1rem', marginTop: 0 }}>
              Audit
            </h2>

            <div style={{ display: 'flex', gap: 8, marginBottom: '1.5rem', borderBottom: '1px solid #e0e0e0' }}>
              <button
                type="button"
                onClick={() => setAuditActiveTab('missing')}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: 6,
                  border: '1px solid ' + (auditActiveTab === 'missing' ? '#1D4E89' : '#e0e0e0'),
                  background: auditActiveTab === 'missing' ? '#1D4E89' : '#fff',
                  color: auditActiveTab === 'missing' ? '#fff' : '#333',
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 500
                }}
              >
                Tab 1: Missing enrollments
              </button>
              <button
                type="button"
                onClick={() => setAuditActiveTab('nonEnrolled')}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: 6,
                  border: '1px solid ' + (auditActiveTab === 'nonEnrolled' ? '#1D4E89' : '#e0e0e0'),
                  background: auditActiveTab === 'nonEnrolled' ? '#1D4E89' : '#fff',
                  color: auditActiveTab === 'nonEnrolled' ? '#fff' : '#333',
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 500
                }}
              >
                Tab 2: Status not &apos;enrolled&apos;
              </button>
            </div>

            {auditActiveTab === 'missing' && (
              <div>
                <p style={{ color: '#666', marginBottom: 16, fontSize: 14 }}>
                  Students who have at least one enrollment in a course group but are missing enrollments in some of that group&apos;s courses.
                </p>
                {missingEnrollmentsAudit.length === 0 ? (
                  <div style={{ padding: '2rem', textAlign: 'center', color: '#666', backgroundColor: '#f8f9fa', borderRadius: 8 }}>
                    No issues found. Every student enrolled in a course group has an enrollment for every course in that group.
                  </div>
                ) : (
                  <>
                    <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={auditIgnoreIfAnyNonEnrolled}
                          onChange={(e) => setAuditIgnoreIfAnyNonEnrolled(e.target.checked)}
                        />
                        <span>Ignore students who have any course in this group with status not &apos;enrolled&apos;</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                        <span>Show only if enrolled in more than</span>
                        <input
                          type="number"
                          min={0}
                          value={auditMinEnrolled}
                          onChange={(e) => setAuditMinEnrolled(e.target.value)}
                          placeholder="(all)"
                          style={{ width: 72, padding: '6px 8px', borderRadius: 6, border: '1px solid #ccc' }}
                        />
                        <span>courses</span>
                      </label>
                      {missingEnrollmentsFiltered.length < missingEnrollmentsAudit.length && (
                        <span style={{ fontSize: 13, color: '#666' }}>
                          (showing {missingEnrollmentsFiltered.length} of {missingEnrollmentsAudit.length})
                        </span>
                      )}
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <p style={{ marginBottom: 12, fontSize: 14, fontWeight: 600, color: '#333' }}>
                        {missingEnrollmentsFiltered.length} result{missingEnrollmentsFiltered.length !== 1 ? 's' : ''}
                      </p>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                        <thead>
                          <tr>
                            <th style={{ ...th, width: 44 }}>
                              <input
                                type="checkbox"
                                checked={missingEnrollmentsFiltered.length > 0 && missingEnrollmentsFiltered.every(row => auditSelectedMissing.has(getAuditRowKey(row)))}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setAuditSelectedMissing(prev => {
                                      const next = new Set(prev);
                                      missingEnrollmentsFiltered.forEach(row => next.add(getAuditRowKey(row)));
                                      return next;
                                    });
                                  } else {
                                    setAuditSelectedMissing(prev => {
                                      const next = new Set(prev);
                                      missingEnrollmentsFiltered.forEach(row => next.delete(getAuditRowKey(row)));
                                      return next;
                                    });
                                  }
                                }}
                                title={missingEnrollmentsFiltered.length > 0 && missingEnrollmentsFiltered.every(row => auditSelectedMissing.has(getAuditRowKey(row))) ? 'Deselect all' : 'Select all'}
                              />
                            </th>
                            <th style={{ ...th, width: 56 }}>#</th>
                            <th style={{ ...th, minWidth: 140 }}>Student</th>
                            <th style={{ ...th, minWidth: 180 }}>Email</th>
                            <th style={th}>Course group</th>
                            <th style={th}>Enrolled</th>
                            <th style={{ ...th, minWidth: 220 }}>Missing courses</th>
                          </tr>
                        </thead>
                        <tbody>
                          {missingEnrollmentsFiltered.length === 0 ? (
                            <tr>
                              <td colSpan={7} style={{ ...td, textAlign: 'center', color: '#666' }}>
                                No rows match the filter. Try lowering &quot;enrolled in more than&quot; or clear it.
                              </td>
                            </tr>
                          ) : (
                            missingEnrollmentsFiltered.map((row, idx) => (
                              <tr key={getAuditRowKey(row)} style={{ borderBottom: '1px solid #eee' }}>
                                <td style={td}>
                                  <input
                                    type="checkbox"
                                    checked={auditSelectedMissing.has(getAuditRowKey(row))}
                                    onChange={(e) => {
                                      const key = getAuditRowKey(row);
                                      setAuditSelectedMissing(prev => {
                                        const next = new Set(prev);
                                        if (e.target.checked) next.add(key);
                                        else next.delete(key);
                                        return next;
                                      });
                                    }}
                                  />
                                </td>
                                <td style={td}>{idx + 1}</td>
                                <td style={td}>{row.studentName}</td>
                                <td style={td}>{row.email}</td>
                                <td style={td}>{row.courseGroupTitle}</td>
                                <td style={td}>{row.enrolledCount} of {row.totalCourses}</td>
                                <td style={td}>
                                  <span style={{ color: '#721c24', fontWeight: 500 }}>
                                    {row.missingCourseTitles.join(', ')}
                                  </span>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                    {missingEnrollmentsFiltered.length > 0 && (() => {
                      const selectedInFiltered = missingEnrollmentsFiltered.filter(row => auditSelectedMissing.has(getAuditRowKey(row))).length;
                      return (
                      <div key="bulk-enroll-bar" style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
                        <button
                          type="button"
                          onClick={handleAuditBulkEnroll}
                          disabled={selectedInFiltered === 0}
                          style={{
                            padding: '0.5rem 1rem',
                            borderRadius: 6,
                            border: '1px solid #28a745',
                            background: selectedInFiltered === 0 ? '#ccc' : '#28a745',
                            color: '#fff',
                            cursor: selectedInFiltered === 0 ? 'not-allowed' : 'pointer',
                            fontSize: 14,
                            fontWeight: 600
                          }}
                        >
                          Bulk enroll selected ({selectedInFiltered} selected)
                        </button>
                        <span style={{ fontSize: 13, color: '#666' }}>
                          Enrolls selected students in their course group&apos;s missing courses using the group&apos;s start dates.
                        </span>
                      </div>
                      );
                    })()}
                  </>
                )}
              </div>
            )}

            {auditActiveTab === 'nonEnrolled' && (
              <div>
                <p style={{ color: '#666', marginBottom: 16, fontSize: 14 }}>
                  Students who have at least one course in a course group with a status other than &apos;enrolled&apos; (e.g. withdrawn, dropped, completed).
                </p>
                {nonEnrolledStatusAudit.length === 0 ? (
                  <div style={{ padding: '2rem', textAlign: 'center', color: '#666', backgroundColor: '#f8f9fa', borderRadius: 8 }}>
                    No issues found. Every course enrollment in every course group has status &apos;enrolled&apos;.
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <p style={{ marginBottom: 12, fontSize: 14, fontWeight: 600, color: '#333' }}>
                      {nonEnrolledStatusAudit.length} result{nonEnrolledStatusAudit.length !== 1 ? 's' : ''}
                    </p>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                      <thead>
                        <tr>
                          <th style={{ ...th, width: 56 }}>#</th>
                          <th style={{ ...th, minWidth: 140 }}>Student</th>
                          <th style={{ ...th, minWidth: 180 }}>Email</th>
                          <th style={th}>Course group</th>
                          <th style={th}>Enrolled</th>
                          <th style={{ ...th, minWidth: 260 }}>Not enrolled (course – status)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {nonEnrolledStatusAudit.map((row, idx) => (
                          <tr key={`${row.enrollmentId}-${row.courseGroupId}`} style={{ borderBottom: '1px solid #eee' }}>
                            <td style={td}>{idx + 1}</td>
                            <td style={td}>{row.studentName}</td>
                            <td style={td}>{row.email}</td>
                            <td style={td}>{row.courseGroupTitle}</td>
                            <td style={td}>{row.enrolledCount} of {row.totalInGroup}</td>
                            <td style={td}>
                              <span style={{ color: '#721c24', fontWeight: 500 }}>
                                {row.nonEnrolledList.map((item, i) => (
                                  <span key={i}>
                                    {i > 0 ? '; ' : ''}{item.course_title} ({item.status})
                                  </span>
                                ))}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const th = { padding: '12px 8px', textAlign: 'left', borderBottom: '2px solid #e0e0e0' };
const td = { padding: '10px 8px', textAlign: 'left', fontSize: 15 };

export default CourseEnrollManager;
